// src/Pages/Admin/agenda/SlotModal.jsx
import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  addDoc,
  doc,
  updateDoc,
  serverTimestamp,
  increment,
  setDoc,
  limit,
  Timestamp,
  runTransaction,
  arrayUnion,
  deleteField,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";

import { db } from "../../../firebase/config";
import {
  X,
  MoreVertical,
  ZoomIn,
  CheckCircle2,
  AlertTriangle,
  Ban,
  CalendarClock,
} from "lucide-react";
import useServicios from "../../../hooks/useServicios";
import ServicioVarianteSelector from "./ServicioVarianteSelector";
import { obtenerSesionesEntrenamiento1x } from "../../../utils/planesEntrenamiento1x";
import { coincidePlanConServicio } from "../../../utils/planServicioMatch";
import { getReglaServicio } from "../../Usuario/Agenda/config/reglasServicios";

/* =========================
    Helper CLP
   ========================= */
const clp = (n) =>
  typeof n === "number" ? `$${n.toLocaleString("es-CL")}` : "—";

// Cuando una reserva utiliza un plan EXISTENTE, el importe de esta
// reserva no es el precio completo del plan guardado en form.monto.
// Utilizar el mismo cálculo en el resumen y al registrar el pago.
const calcularCobroPlanExistente = (plan, tipoUsoPlan) => {
  if (!plan) return 0;
  const saldo = Math.max(0, Number(plan.monto_restante ?? 0) || 0);
  const tipo = tipoUsoPlan === "ninguno"
    ? (saldo > 0 ? "valor_sesion" : "incluida")
    : tipoUsoPlan;

  if (tipo === "saldo_plan") return saldo;
  if (tipo === "valor_sesion") {
    const precioSesion = Math.max(0, Number(plan.precio_por_sesion ?? 0) || 0);
    return Math.min(precioSesion, saldo); // nunca cobrar más que el saldo
  }
  return 0; // sesión incluida en plan pagado
};

/* =========================
    Utilidades RUT
   ========================= */
const normalizeRut = (rut) =>
  (rut || "").toString().replace(/\./g, "").replace(/-/g, "").toUpperCase().trim();

const formatRut = (rut) => {
  const n = normalizeRut(rut);
  if (!n) return "";
  const body = n.slice(0, -1);
  const dv = n.slice(-1);
  return `${Number(body).toLocaleString("es-CL")}-${dv}`;
};

  const isValidRut = (rut) => {
    const n = normalizeRut(rut);
    if (n.length < 2) return false;
    const body = n.slice(0, -1);
    const dv = n.slice(-1).toUpperCase();
    let sum = 0,
      m = 2;
    for (let i = body.length - 1; i >= 0; i--) {
      sum += Number(body[i]) * m;
      m = m === 7 ? 2 : m + 1;
    }
    const res = 11 - (sum % 11);
    const dvCalc = res === 11 ? "0" : res === 10 ? "K" : String(res);
    return dv === dvCalc;
  };

  const toDateFromYmdHm = (ymd, hhmm) => {
  const [y, m, d] = (ymd || "").split("-").map(Number);
  const [hh, mm] = (hhmm || "").split(":").map(Number);
  return new Date(y, (m || 1) - 1, d || 1, hh || 0, mm || 0, 0, 0);
};

// =====================================================
// REPROGRAMACIÓN ADMINISTRATIVA — reglas de ocupación EKINE
// No crea nuevas reservas, cobros ni movimientos de planes.
// Conserva 2 cupos simultáneos para sesiones grupales y
// 30 minutos de bloqueo previo para sesiones privadas.
// =====================================================
const RESERVAS_QUE_BLOQUEAN = new Set([
  "pendiente", "aprobada", "realizada", "tentativa_break",
]);

const esFechaYmdValida = (fecha) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(fecha || "")) return false;
  const [y, m, d] = fecha.split("-").map(Number);
  const comprobacion = new Date(y, m - 1, d);
  return comprobacion.getFullYear() === y &&
    comprobacion.getMonth() === m - 1 && comprobacion.getDate() === d;
};

const seSuperponen = (aInicio, aFin, bInicio, bFin) =>
  aInicio < bFin && bInicio < aFin;

const obtenerOcupacion = (r, fecha) => {
  const regla = getReglaServicio(
    r?.servicio_categoria || r?.categoria || "",
    r?.servicio_variante || r?.variante || "",
    r?.modalidad || "",
    r?.esPrimeraSesion === true
  ) || {};

  // En pareja y primera sesión: ocupación privada, aunque un registro
  // antiguo no tenga el flag explícito permiteTopar.
  const texto = `${r?.servicio_categoria || ""} ${r?.servicio_variante || ""}`.toLowerCase();
  const esPareja = r?.es_pareja === true || texto.includes("pareja");
  const privada = esPareja || r?.esPrimeraSesion === true ||
    r?.permiteTopar === false ||
    String(r?.tipoSesion || "").toLowerCase().includes("exclu") ||
    regla.permiteTopar !== true;

  const duracion = Math.max(30, Number(r?.duracionMin ?? r?.durationMin ?? regla.duracionMin ?? 60) || 60);
  const previo = privada
    ? Math.max(0, Number(r?.bufferPrevioMin ?? 0) || 0, Number(regla.bloqueaPrevio ?? 30) || 0)
    : 0;
  const inicio = toDateFromYmdHm(fecha, r.hora).getTime();
  return {
    inicio,
    fin: inicio + duracion * 60000,
    bloqueoInicio: inicio - previo * 60000,
    privada,
    capacidad: Math.max(1, Number(r?.capacidadMax ?? regla.capacidadMax ?? 2) || 2),
  };
};

const validarHorarioReprogramado = (reserva, fechaNueva, horaNueva, reservasDelDia, breaksDelDia) => {
  const nueva = obtenerOcupacion({ ...reserva, hora: horaNueva }, fechaNueva);
  const otras = (reservasDelDia || [])
    .filter((r) => r.id !== reserva.id &&
      (RESERVAS_QUE_BLOQUEAN.has(String(r.estado || "").toLowerCase()) || r.esTentativa === true) &&
      r.hora)
    .map((r) => obtenerOcupacion(r, fechaNueva));

  // Los breaks se aplican solo durante la sesión, tal como hace DayAgenda.
  for (const b of breaksDelDia || []) {
    if (!b.start || !b.end) continue;
    const ini = toDateFromYmdHm(fechaNueva, b.start).getTime();
    const fin = toDateFromYmdHm(fechaNueva, b.end).getTime();
    if (seSuperponen(nueva.inicio, nueva.fin, ini, fin)) {
      throw new Error("El horario se superpone con un descanso o bloqueo de agenda.");
    }
  }

  if (nueva.privada) {
    if (otras.some((r) => seSuperponen(nueva.bloqueoInicio, nueva.fin, r.bloqueoInicio, r.fin))) {
      throw new Error("Este horario se superpone con otra reserva o su bloqueo previo.");
    }
    return;
  }

  // Las grupales pueden coincidir hasta su capacidad (EKINE: 2).
  // Nunca pueden coincidir con una sesión privada ni su bloqueo previo.
  if (otras.some((r) => r.privada && seSuperponen(nueva.inicio, nueva.fin, r.bloqueoInicio, r.fin))) {
    throw new Error("Hay una sesión privada o bloqueo previo en ese horario.");
  }

  const grupos = otras.filter((r) => !r.privada &&
    seSuperponen(nueva.inicio, nueva.fin, r.inicio, r.fin));
  const extremos = [...new Set([
    nueva.inicio, nueva.fin,
    ...grupos.flatMap((r) => [Math.max(nueva.inicio, r.inicio), Math.min(nueva.fin, r.fin)]),
  ])].sort((a, b) => a - b);

  for (let i = 0; i < extremos.length - 1; i++) {
    const t = extremos[i];
    const simultaneas = grupos.filter((r) => r.inicio <= t && t < r.fin);
    const capacidad = Math.min(nueva.capacidad, ...simultaneas.map((r) => r.capacidad));
    if (simultaneas.length + 1 > capacidad) {
      throw new Error(`Este horario ya alcanzó la capacidad de ${capacidad} reserva(s) grupal(es).`);
    }
  }
};

// =====================================================
// Helpers EKINE (servicio base + categoría regla)
// =====================================================
const stripPeriodoServicio = (t) =>
  (t || "")
    .replace(/[—–-]\s*(mensual|trimestral|semestral)\b/gi, "")
    .replace(/\s+/g, " ")
    .trim();

const inferCategoriaRegla = (t) => {
  const n = (t || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

  // ✅ Primero detectar "pareja" para que no caiga en "entrenamiento" genérico
  if (n.includes("pareja")) return "Entrenamiento en Pareja";

  // ✅ Diferenciar Planes vs Entrenamiento suelto (si tu UI los separa)
  if (n.includes("plan") && n.includes("entren")) return "Planes de Entrenamiento";

  // ✅ Entrenamiento normal (si existe)
  if (n.includes("entren")) return "Entrenamiento";

  if (n.includes("kinesiolog")) return "Kinesiología";
  if (n.includes("recovery")) return "Recovery";
  if (n.includes("masaj") || n.includes("masoterapia")) return "Masoterapia";

  return "Servicio";
};

const PRECIO_PRIMERA_SESION_PLAN = 30000;

const getClavePrimeraSesion = (categoria = "", variante = "") => {
  const c = (categoria || "").toLowerCase();
  const v = (variante || "").toLowerCase();

  if (c.includes("masoter") || v.includes("masoter")) return "masoterapia";
  if (c.includes("masaj") || v.includes("masaj")) return "masajes";
  if (c.includes("reco")) return "recovery";
  if (c.includes("entren")) return "entrenamiento";
  if (c.includes("kine")) return "kinesiologia";

  return null;
};

const calcularPrecioSesionPosteriorPlan = (precioTotal, totalSesiones) => {
  if (totalSesiones <= 1) return precioTotal || 0;

  const restante = Math.max(0, (precioTotal || 0) - PRECIO_PRIMERA_SESION_PLAN);
  return Math.round(restante / (totalSesiones - 1));
};

// ===============================
// Helpers cupón (igual que agenda.jsx)
// ===============================
const normKey = (s) =>
  (s || "")
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const normLoose = (t) =>
  (t || "")
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[—–-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const makeVarianteTokenRaw = (servicioId, varianteKey) =>
  `${servicioId}::${normKey(varianteKey)}`;

const makeVarianteTokenNorm = (servicioId, varianteKey) =>
  `${normKey(servicioId)}::${normKey(varianteKey)}`;


// =====================================================
// Helpers: contadores de plan desde reservas (EKINE)
// Cuentan cupo: pendiente + aprobada + realizada
// NO cuentan: cancelada + rechazada
// =====================================================
const contarSesionesPlanDesdeReservas = async (planId) => {
  if (!planId) return { usadas: 0, reservadas: 0, ocupadas: 0 };

  const qR = query(collection(db, "reservas"), where("planId", "==", planId));
  const s = await getDocs(qR);
  const reservas = s.docs.map((d) => d.data());

  const usadas = reservas.filter((r) => r.estado === "realizada").length; // ✅ realizadas
  const reservadas = reservas.filter((r) =>
    ["pendiente", "aprobada"].includes(r.estado)
  ).length; // ✅ bloquean cupo

  const ocupadas = usadas + reservadas; // ✅ pendiente+aprobada+realizada

  return { usadas, reservadas, ocupadas };
};

const syncPlanCounters = async (planId) => {
  if (!planId) return;

  const { usadas, reservadas } = await contarSesionesPlanDesdeReservas(planId);

  await updateDoc(doc(db, "planes_usuarios", planId), {
    sesiones_usadas: usadas,
    sesiones_reservadas: reservadas,
    sesiones_realizadas: usadas, // opcional compat
    updatedAt: serverTimestamp(),
  });
};

// 🔵 Normalizador de categoría para detectar Kinesiología 1 sesión
  const normalizarCategoriaServicio = (t) =>
    (t || "")
      .toLowerCase()
      .replace(/sesiones de /g, "")
      .replace(/planes? de /g, "")
      .trim();

  /* ======================================================
      COMPONENTE PRINCIPAL
  ====================================================== */
  export default function SlotModal({ isOpen, slot, onClose, onRefresh, onReprogramada, readOnly = false }) {
    const [mode, setMode] = useState("new");
    const [loading, setLoading] = useState(false);
    const [reserva, setReserva] = useState(null);
    const [pago, setPago] = useState(null);
    const [manualHora, setManualHora] = useState("");
  const [showReprogramar, setShowReprogramar] = useState(false);
  const [nuevaFechaReserva, setNuevaFechaReserva] = useState("");
  const [nuevaHoraReserva, setNuevaHoraReserva] = useState("");
  const [motivoReprogramacion, setMotivoReprogramacion] = useState("");

    const [pacienteQuery, setPacienteQuery] = useState("");
    const [pacienteOk, setPacienteOk] = useState(false);
    const [pacienteResultados, setPacienteResultados] = useState([]);
    const [autofillDone, setAutofillDone] = useState(false);

    const [form, setForm] = useState({
      nombres: "",
      apellidos: "",
      rut: "",
      email: "",
      telefono: "+569",

      servicioId: "",
      notas: "",
      pagoEstado: "pendiente",

      // 🔵 Kinesiología
      modalidad: "grupal", // grupal | privada

      monto: 0,

      // 🟣 Entrenamiento en pareja (solo si aplica)
      pareja: {
        nombres: "",
        apellidos: "",
        rut: "",
        email: "",
        telefono: "",
      },
    });

  // ===============================
  // Cupón (manual admin)
  // ===============================
  const [cuponCode, setCuponCode] = useState("");
  const [cuponInfo, setCuponInfo] = useState(null);
  const [cuponMsg, setCuponMsg] = useState("");
  const [cuponLoading, setCuponLoading] = useState(false);

  const [profesionales, setProfesionales] = useState([]);
  const [profesionalId, setProfesionalId] = useState("");

  const { serviciosFlat, loading: loadingServicios } = useServicios();

  const serviciosSource = (serviciosFlat || []).map((v) => {
    const servicioBase = stripPeriodoServicio(v.categoria); // "Planes de Entrenamiento"
    const categoriaRegla = inferCategoriaRegla(servicioBase);

    // ✅ 1) Servicio real (docId) (si existe en flat)
    const servicioDocId =
      v.servicioDocId || v.servicioId || v.servicio || v.categoriaId || "";

    // ✅ 2) Variante real (id estable)
    const varianteId =
      v.varianteId || v.id || ""; // OJO: si tu flat NO trae varianteId, se queda como v.id

    return {
      // id del item (lo usas en el select)
      id: v.id,

      // ✅ claves estables para cupones
      servicioDocId: servicioDocId || normKey(servicioBase), // fallback razonable
      varianteId: varianteId,

      categoria: v.categoria,
      servicioBase,
      categoriaRegla,
      variante: v.variante,
      precio: v.precio ?? 0,

      precio_grupo: v.precio_grupo ?? null,
      precio_privada: v.precio_privada ?? null,

      modalidadesPermitidas: v.modalidadesPermitidas ?? null,

      display: v.display,
      raw: `${servicioBase} — ${v.variante}`,
    };
  });

  const precioDeServicioId = (sid) => {
    const s = serviciosSource.find((x) => x.id === sid);
    return s?.precio || 0;
  };

  // 🔵 Precio dinámico Kinesiología (según modalidad)
// 🔵 Precio dinámico Kinesiología desde variantes
const obtenerPrecioKinesiologia = (servicio, modalidad) => {
  if (!servicio) return 0;

  // 🔒 Solo aplica a Kinesiología
  const cat = normalizarCategoriaServicio(servicio.categoria);
  if (cat !== "kinesiología") {
    return servicio.precio ?? 0;
  }

  // 🔵 Si la variante trae precios por modalidad → usar esos
  if (modalidad === "privada") {
    return servicio.precio_privada ?? servicio.precio ?? 0;
  }

  // default grupal
  return servicio.precio_grupo ?? servicio.precio ?? 0;
};




  // 🔵 NUEVO: info de paciente + planes
  const [pacienteUid, setPacienteUid] = useState(null);          // uid auth del paciente
  const [planesPaciente, setPlanesPaciente] = useState([]);      // planes_usuarios del paciente
  const [planSeleccionado, setPlanSeleccionado] = useState(null); // plan elegido para esta reserva
  const [tipoUsoPlan, setTipoUsoPlan] = useState("ninguno");     // "ninguno" | "valor_sesion" | "saldo_plan" | "incluida"

  // UI
  const [showOpciones, setShowOpciones] = useState(false);
  const [showSubModal, setShowSubModal] = useState(false);
  const [subAction, setSubAction] = useState("reprogramar");
  const [subMotivo, setSubMotivo] = useState("");
  const [showSesionDropdown, setShowSesionDropdown] = useState(false);
  const [zoomed, setZoomed] = useState(null);
  const [planInfo, setPlanInfo] = useState(null);

  // 🔵 Modal de reembolso tras cancelar reserva de plan
  const [showReembolsoModal, setShowReembolsoModal] = useState(false);
  const [reembolsoMonto, setReembolsoMonto] = useState("");
  const [reembolsoMotivo, setReembolsoMotivo] = useState("");
  const [reembolsoLoading, setReembolsoLoading] = useState(false);
  const [showAprobarCancelMenu, setShowAprobarCancelMenu] = useState(false);
  const [showReembolsoUI, setShowReembolsoUI] = useState(false);


  // Toast
  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);
  const dropdownRef = useRef(null);
  const sesionDropdownRef = useRef(null);

  // 🔔 Solicitud de cambio de horario pendiente (si existe)
  const [solicitudCambioPendiente, setSolicitudCambioPendiente] = useState(null);

  const [solicitudCancelacion, setSolicitudCancelacion] = useState(null);
  const [respuestaAdmin, setRespuestaAdmin] = useState("");

  const loadSolicitudCancelacionPendiente = useCallback(async (reservaId) => {
  try {
    if (!reservaId) {
      setSolicitudCancelacion(null);
      return;
    }

    const qSol = query(
      collection(db, "solicitudes_modificacion"),
      where("reservaId", "==", reservaId),
      where("tipo", "==", "cancelacion"),
      where("estado", "==", "pendiente")
    );

    const s = await getDocs(qSol);

    if (!s.empty) {
      const d = s.docs[0];
      setSolicitudCancelacion({ id: d.id, ...d.data() });
    } else {
      setSolicitudCancelacion(null);
    }
  } catch (err) {
    console.error("Error cargando solicitud de cancelación:", err);
    setSolicitudCancelacion(null);
  }
}, []);

const generarVariantesRut = (rut) => {
  const limpio = normalizeRut(rut);
  const body = limpio.slice(0, -1);
  const dv = limpio.slice(-1);

  const conGuion = `${body}-${dv}`;
  const conGuionDVUpper = `${body}-${dv.toUpperCase()}`;
  const conGuionDVLower = `${body}-${dv.toLowerCase()}`;

  const sinGuion = `${body}${dv}`;
  const sinGuionUpper = `${body}${dv.toUpperCase()}`;
  const sinGuionLower = `${body}${dv.toLowerCase()}`;

  return [
    conGuion,
    conGuionDVUpper,
    conGuionDVLower,
    sinGuion,
    sinGuionUpper,
    sinGuionLower,
  ];
};


  /* =========================
      Hooks: setup inicial
  ========================= */
  useEffect(() => {
    const esc = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);

  // Manejo global de clics fuera de menús
  useEffect(() => {
    const clickOut = (e) => {
      const target = e.target;
      const insideOpciones = dropdownRef.current?.contains(target);
      const insideSesion = sesionDropdownRef.current?.contains(target);
      if (!insideOpciones && !insideSesion) {
        setShowOpciones(false);
        setShowSesionDropdown(false);
      }
    };
    document.addEventListener("mousedown", clickOut);
    return () => document.removeEventListener("mousedown", clickOut);
  }, []);

  // Manejo global de errores
  useEffect(() => {
    const handleError = (error) => console.error("Error en SlotModal:", error);
    window.addEventListener("error", handleError);
    return () => window.removeEventListener("error", handleError);
  }, []);

  /* =========================
      Carga de datos
  ========================= */
  const loadProfesionales = useCallback(async () => {
    try {
      const q = query(collection(db, "profesionales"), where("activo", "==", true));
      const s = await getDocs(q);
      const list = s.docs.map((d) => {
        const u = d.data();
        return {
          id: u.uid || d.id,
          nombre: u.nombre || "(sin nombre)",
          correo: u.correo || "",
        };
      });
      setProfesionales(list.sort((a, b) => a.nombre.localeCompare(b.nombre, "es")));
    } catch (err) {
      console.error("Error al cargar profesionales:", err);
    }
  }, []);

  // Cargar plan asociado a la reserva (si existe) para la vista detalle
  useEffect(() => {
    const loadPlan = async () => {
      if (!reserva?.desdePlan || !reserva?.planId) {
        setPlanInfo(null);
        return;
      }

      try {
        const ref = doc(db, "planes_usuarios", reserva.planId);
        const snap = await getDoc(ref);

        if (snap.exists()) {
          setPlanInfo({ id: snap.id, ...snap.data() });
        }
      } catch (err) {
        console.error("Error cargando el plan en SlotModal:", err);
      }
    };

    loadPlan();
  }, [reserva]);

  const loadSolicitudCambioPendiente = useCallback(async (reservaId) => {
    try {
      if (!reservaId) {
        setSolicitudCambioPendiente(null);
        return;
      }
      const qSol = query(
        collection(db, "solicitudes_modificacion"),
        where("reservaId", "==", reservaId),
        where("tipo", "==", "cambio_horario"),
        where("estado", "==", "pendiente")
      );
      const s = await getDocs(qSol);
      if (!s.empty) {
        const d = s.docs[0];
        setSolicitudCambioPendiente({ id: d.id, ...d.data() });
      } else {
        setSolicitudCambioPendiente(null);
      }
    } catch (err) {
      console.error("Error cargando solicitud de cambio:", err);
      setSolicitudCambioPendiente(null);
    }
  }, []);

  const loadReserva = useCallback(async () => {
    try {
      if (!slot) return;
      setLoading(true);
      let r = null;

      if (slot?.reservaId) {
        const ref = doc(db, "reservas", slot.reservaId);
        const snap = await getDoc(ref);
        if (snap.exists()) r = { id: snap.id, ...snap.data() };
      }

      if (!r && slot?.dateISO && slot?.start) {
        const q = query(
          collection(db, "reservas"),
          where("fecha", "==", slot.dateISO),
          where("hora", "==", slot.start),
          where("estado", "in", ["aprobada", "pendiente", "realizada"])
        );
        const s = await getDocs(q);
        if (!s.empty) {
          const d = s.docs[0];
          r = { id: d.id, ...d.data() };
        }
      }

      if (r) {
        setReserva(r);
        if (r?.pagoId) {
          const pSnap = await getDoc(doc(db, "pagos", r.pagoId));
          if (pSnap.exists()) setPago({ id: pSnap.id, ...pSnap.data() });
        }
        if (r?.profesionalId) setProfesionalId(r.profesionalId);

        // 🔎 carga solicitud de cambio si existe
        await loadSolicitudCambioPendiente(r.id);
        await loadSolicitudCancelacionPendiente(r.id);
      } else {
        setReserva(null);
        setPago(null);
        setSolicitudCambioPendiente(null);
      }
    } catch (err) {
      console.error("Error en loadReserva:", err);
    } finally {
      setLoading(false);
    }}, 
    [slot, loadSolicitudCambioPendiente, loadSolicitudCancelacionPendiente]);


    useEffect(() => {
      if (!isOpen || !slot) return;

      // ✅ Si está abierto el reembolso, NO resetees todo aunque cambie el slot por snapshot/refresh
      if (showReembolsoModal || showReembolsoUI || showReprogramar) return;

      const m = slot.status === "reserved" ? "detail" : "new";
      setMode(m);
      setReserva(null);
      setPago(null);
      setPacienteQuery("");
      setPacienteOk(false);
      setPacienteResultados([]);
      setAutofillDone(false);
      setManualHora(slot?.start || "07:30");
      setShowSesionDropdown(false);
      setShowOpciones(false);
      setShowSubModal(false);
      setShowReprogramar(false);
      setNuevaFechaReserva("");
      setNuevaHoraReserva("");
      setMotivoReprogramacion("");
      setSubAction("cancelar");
      setSubMotivo("");

      setShowReembolsoModal(false);
      setReembolsoMonto("");
      setReembolsoMotivo("");

      setPacienteUid(null);
      setPlanesPaciente([]);
      setPlanSeleccionado(null);
      setTipoUsoPlan("ninguno");

      loadProfesionales();
      setCuponCode("");
      setCuponInfo(null);
      setCuponMsg("");
      setCuponLoading(false);

      if (m === "detail") loadReserva();
    }, [
      isOpen,

      slot?.reservaId,
      slot?.status,
      slot?.dateISO,
      slot?.start,

      loadProfesionales,
      loadReserva,
      showReembolsoModal,
      showReembolsoUI,
      showReprogramar,
    ]);

  /* =========================
      Toast helper
  ========================= */
  const showToast = (text) => {
    setToast({ text });
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 2400);
  };

  /* =========================
      Handlers principales
  ========================= */
  const handleAprobar = async () => {
    if (!reserva?.id) return;

    const profesional = profesionales.find((p) => p.id === profesionalId);

    // Si no hay profesional, igual se aprueba
    const dataUpdate = {
      estado: "aprobada",
      updatedAt: serverTimestamp(),
    };

    if (profesionalId) {
      dataUpdate.profesionalId = profesionalId;
      dataUpdate.profesionalNombre = profesional?.nombre || "";
    }

  try {
    const profesional = profesionales.find((p) => p.id === profesionalId);

    // 1) actualizar la reserva
    await updateDoc(doc(db, "reservas", reserva.id), {
      estado: "aprobada",
      profesionalId,
      profesionalNombre: profesional?.nombre || "",
      updatedAt: serverTimestamp(),
    });

    // 2) asegurar profesionalId en la colección usuarios
    const emailKey = (reserva?.email || "").trim().toLowerCase();
    if (profesionalId && emailKey) {
      const refUsuario = doc(db, "usuarios", emailKey);
      await setDoc(
        refUsuario,
        {
          profesionalId,
          profesionalNombre: profesional?.nombre || "",
          profesionalAsignadoEn: serverTimestamp(),
        },
        { merge: true }
      );
    }

    showToast("Reserva aprobada correctamente");
    onRefresh?.();
    onClose?.();
  } catch (err) {
    console.error("Error al aprobar reserva:", err);
    alert("No se pudo aprobar la reserva.");
  }
};


    const handleRechazar = async () => {
      if (!reserva?.id) return;
      const motivo = prompt("Motivo del rechazo:");
      if (!motivo) return;

      try {
        await updateDoc(doc(db, "reservas", reserva.id), {
          estado: "rechazada",
          rechazoMotivo: motivo,
          updatedAt: serverTimestamp(),
        });

        // ✅ Si era de plan: recontar (rechazada NO debe contar cupo)
        if (reserva?.desdePlan && reserva?.planId) {
          await syncPlanCounters(reserva.planId);
        }

        showToast("Reserva rechazada");
        onRefresh?.();
        onClose?.();
      } catch (e) {
        console.error(e);
        alert("No se pudo rechazar la reserva.");
      }
    };

    const handleAprobarCancelacionSinReembolso = async () => {
      if (!reserva?.id) return alert("No hay reserva.");
      if (!solicitudCancelacion?.id) return alert("Solicitud no cargada.");

      if (!respuestaAdmin.trim()) {
        return alert("Debes escribir una respuesta para el paciente.");
      }

      try {
        // Actualizar reserva
        await updateDoc(doc(db, "reservas", reserva.id), {
          estado: "cancelada",
          motivo_admin_paciente: solicitudCancelacion.motivo,
          motivo_admin_respuesta: respuestaAdmin,
          updatedAt: serverTimestamp(),
        });

        // Actualizar solicitud
        await updateDoc(
          doc(db, "solicitudes_modificacion", solicitudCancelacion.id),
          {
            estado: "procesada",
            respuesta_admin: respuestaAdmin,
            procesadaEn: serverTimestamp(),
            procesadaPor: reserva?.profesionalNombre || "Administrador",
          }
        );

        if (reserva?.desdePlan && reserva?.planId) {
          await syncPlanCounters(reserva.planId);
        }

        showToast("Cancelación aprobada sin reembolso");
        onRefresh?.();
        onClose?.();
      } catch (err) {
        console.error("Error:", err);
        alert("No se pudo aprobar la cancelación.");
      }
    };



    const handleAprobarCancelacionConReembolso = async () => {
      if (!reserva?.id) return alert("No hay reserva.");
      if (!solicitudCancelacion?.id) return alert("Solicitud no cargada.");

      if (!respuestaAdmin.trim()) {
        return alert("Debes escribir una respuesta para el paciente.");
      }

      const monto = Number(reembolsoMonto);
      if (!monto || monto <= 0) return alert("Ingresa un monto válido.");

      try {
        // Cancelar reserva
        await updateDoc(doc(db, "reservas", reserva.id), {
          estado: "cancelada",
          motivo_admin_paciente: solicitudCancelacion.motivo,
          motivo_admin_respuesta: respuestaAdmin,
          updatedAt: serverTimestamp(),
        });

        // Si pertenece a un plan → reembolsa saldo
        if (reserva?.planId) {
          const planRef = doc(db, "planes_usuarios", reserva.planId);

          await updateDoc(planRef, {
            pago_total_realizado: increment(-monto),
            monto_restante: increment(monto),
          });

          // Registrar movimiento
          await addDoc(collection(db, "planes_movimientos"), {
            planId: reserva.planId,
            reservaId: reserva.id,
            tipo: "reembolso",
            monto,
            motivo: "Reembolso por cancelación aprobada",
            fecha: serverTimestamp(),
          });

          if (reserva?.desdePlan && reserva?.planId) {
            await syncPlanCounters(reserva.planId);
          }
        }

        // Actualizar solicitud
        await updateDoc(
          doc(db, "solicitudes_modificacion", solicitudCancelacion.id),
          {
            estado: "procesada",
            respuesta_admin: respuestaAdmin,
            procesadaEn: serverTimestamp(),
            procesadaPor: reserva?.profesionalNombre || "Administrador",
          }
        );

        showToast("Cancelación aprobada con reembolso.");
        setShowReembolsoUI(false);
        onRefresh?.();
        onClose?.();
      } catch (err) {
        console.error("❌ Error aprobando con reembolso:", err);
        alert("No se pudo procesar el reembolso.");
      }
    };

    const handleAprobarCancelacion = async () => {
    if (!reserva?.id) {
  console.log("❌ No hay reserva.id");
  return;
}

if (!solicitudCancelacion) {
  console.log("❌ solicitudCancelacion aún no cargada");
  return;
}

    try {
      await updateDoc(doc(db, "reservas", reserva.id), {
        estado: "cancelada",
        motivo_admin: solicitudCancelacion.motivo,
        updatedAt: serverTimestamp(),
      });

      await updateDoc(doc(db, "solicitudes_modificacion", solicitudCancelacion.id), {
        estado: "procesada",
        procesadaEn: serverTimestamp(),
        procesadaPor: reserva?.profesionalNombre || "Administrador",
      });

      if (reserva?.desdePlan && reserva?.planId) {
        await syncPlanCounters(reserva.planId);
      }

      showToast("Cancelación aprobada correctamente");
      onRefresh?.();
      onClose?.();
    } catch (err) {
      console.error("Error aprobando cancelación:", err);
      alert("No se pudo aprobar la cancelación.");
    }
  };

    const handleRechazarCancelacion = async () => {
      if (!reserva?.id) return alert("No hay reserva.");
      if (!solicitudCancelacion?.id) return alert("Solicitud no cargada.");

      if (!respuestaAdmin.trim()) {
        return alert("Debes escribir una respuesta para el paciente.");
      }

      try {
        await updateDoc(
          doc(db, "solicitudes_modificacion", solicitudCancelacion.id),
          {
            estado: "rechazada",
            respuesta_admin: respuestaAdmin,
            procesadaEn: serverTimestamp(),
            procesadaPor: reserva?.profesionalNombre || "Administrador",
          }
        );

        await updateDoc(doc(db, "reservas", reserva.id), {
          motivo_admin_respuesta: respuestaAdmin,
          updatedAt: serverTimestamp(),
        });

        showToast("Solicitud de cancelación rechazada.");
        onRefresh?.();
      } catch (err) {
        console.error("Error rechazando cancelación:", err);
        alert("No se pudo rechazar la solicitud.");
      }
    };

  const handleSesionEstado = async (estado) => {
    if (!reserva?.id) return;
    setLoading(true);
    try {
      const nuevoEstado =
        estado === "completada" || estado === "realizada" ? "realizada" : reserva.estado;

      await updateDoc(doc(db, "reservas", reserva.id), {
        estadoSesion: estado,
        estado: nuevoEstado,
        updatedAt: serverTimestamp(),
      });

      // ✅ Si es de plan: al marcar realizada/completada cambia cupo real
      if (reserva?.desdePlan && reserva?.planId) {
        await syncPlanCounters(reserva.planId);
      }

      setReserva((r) => ({ ...r, estadoSesion: estado, estado: nuevoEstado }));
      setShowSesionDropdown(false);
      showToast(
        `Sesión marcada como: ${
          estado === "completada"
            ? "COMPLETADA (paciente asistió)"
            : "REALIZADA (paciente no asistió)"
        }`
      );
      onRefresh?.();
    } catch (err) {
      alert("No se pudo actualizar el estado de sesión.");
    } finally {
      setLoading(false);
    }
  };

  const handleApprovePago = async () => {
    if (!pago?.id) return;
    try {
      await updateDoc(doc(db, "pagos", pago.id), {
        estado: "aprobado",
        aprobadoEn: serverTimestamp(),
      });
      showToast("Pago aprobado correctamente");
      onRefresh?.();
      const pSnap = await getDoc(doc(db, "pagos", pago.id));
      if (pSnap.exists()) setPago({ id: pSnap.id, ...pSnap.data() });
    } catch {
      alert("No se pudo aprobar el pago.");
    }
  };

  // 🔵 Marcar pago completo (servicio suelto o plan con saldo)
  const handleMarcarPagoCompleto = async () => {
    if (!reserva?.id) return;

    const confirmar = window.confirm(
      "¿Estás seguro de marcar esta sesión pagada completamente?"
    );
    if (!confirmar) return;

    try {
      // === CASO: NO viene de plan ===
      if (!reserva.desdePlan) {
        const montoTotal =
          reserva.precio_total || reserva.monto || pago?.monto_total || 0;

        await updateDoc(doc(db, "reservas", reserva.id), {
          pago_tipo: "pago100",
          pagoEstado: "completo",
          monto_pagado: montoTotal,
          fecha_pago_completo: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });

        setReserva((r) =>
          r
            ? {
                ...r,
                pago_tipo: "pago100",
                pagoEstado: "completo",
                monto_pagado: montoTotal,
              }
            : r
        );

        showToast("Pago completo marcado");
        onRefresh?.();
        return;
      }

      // === CASO: VIENE DE PLAN ===
      const planId = reserva.planId;
      const saldoPendiente = reserva.monto_restante ?? 0;
      const monto = saldoPendiente > 0 ? saldoPendiente : reserva.precio_total || 0;

      await updateDoc(doc(db, "reservas", reserva.id), {
        pago_tipo: "pago_total_restante",
        pagoEstado: "completo",
        monto_pagado: (reserva.monto_pagado || 0) + monto,
        monto_restante: 0,
        fecha_pago_completo: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      if (planId && monto > 0) {
        const planRef = doc(db, "planes_usuarios", planId);
        try {
          await updateDoc(planRef, {
            pago_total_realizado: increment(monto),
            monto_restante: 0,
          });
        } catch (e) {
          console.error("Error actualizando plan al marcar pago completo:", e);
        }

        // Movimiento de pago en planes_movimientos
        try {
          await addDoc(collection(db, "planes_movimientos"), {
            planId,
            reservaId: reserva.id,
            tipo: "pago",
            monto: Math.abs(monto),
            motivo: "Pago saldo restante registrado desde SlotModal",
            fecha: serverTimestamp(),
          });
        } catch (e) {
          console.error("Error registrando movimiento de pago:", e);
        }
      }

      setReserva((r) =>
        r
          ? {
              ...r,
              pago_tipo: "pago_total_restante",
              pagoEstado: "completo",
              monto_pagado: (r.monto_pagado || 0) + monto,
              monto_restante: 0,
            }
          : r
      );

      showToast("Pago del plan marcado como completo");
      onRefresh?.();
    } catch (err) {
      console.error("Error al marcar pago completo:", err);
      alert("No se pudo marcar como pago completo.");
    }
  };

  // 🔵 Reembolso de plan
  const registrarReembolsoPlan = async (monto, motivoExtra) => {
    if (!reserva?.planId || !reserva?.id) return;
    const montoAbs = Math.abs(monto || 0);
    if (!montoAbs) {
      alert("El monto de reembolso debe ser mayor que 0.");
      return;
    }

    setReembolsoLoading(true);
    try {
      const planRef = doc(db, "planes_usuarios", reserva.planId);

      await addDoc(collection(db, "planes_movimientos"), {
        planId: reserva.planId,
        reservaId: reserva.id,
        tipo: "reembolso",
        monto: montoAbs,
        motivo:
          motivoExtra ||
          reembolsoMotivo ||
          `Reembolso asociado a la reserva ${reserva.id}`,
        fecha: serverTimestamp(),
      });

      await updateDoc(planRef, {
        pago_total_realizado: increment(-montoAbs),
        monto_restante: increment(montoAbs),
      });

      showToast("Reembolso registrado en el plan.");
      setShowReembolsoModal(false);
      setReembolsoMonto("");
      setReembolsoMotivo("");
      onRefresh?.();
      onClose?.();
    } catch (e) {
      console.error("Error registrando reembolso del plan:", e);
      alert("No se pudo registrar el reembolso, revisa la consola.");
    } finally {
      setReembolsoLoading(false);
    }
  };

  // =====================================================
  // Reprogramar: mismo documento y mismo plan; nunca cancelar/crear.
  // =====================================================
  const handleReprogramarReserva = async () => {
    if (readOnly || !reserva?.id || loading) return;

    const fechaNueva = (nuevaFechaReserva || "").trim();
    const horaNueva = (nuevaHoraReserva || "").trim();
    if (!esFechaYmdValida(fechaNueva)) return alert("Selecciona una fecha válida.");
    if (!/^([01]\d|2[0-3]):(00|30)$/.test(horaNueva)) {
      return alert("Selecciona una hora válida en intervalos de 30 minutos.");
    }

    const inicioNuevo = toDateFromYmdHm(fechaNueva, horaNueva);
    if (inicioNuevo.getTime() <= Date.now()) {
      return alert("No puedes reprogramar una reserva para una fecha u hora pasada.");
    }
    if (fechaNueva === reserva.fecha && horaNueva === reserva.hora) {
      return alert("Selecciona una fecha u hora distinta a la actual.");
    }
    if (!["pendiente", "aprobada"].includes(reserva.estado) ||
      ![null, undefined, "pendiente"].includes(reserva.estadoSesion)) {
      return alert("Solo puedes reprogramar reservas pendientes que no hayan sido realizadas ni canceladas.");
    }
    if (solicitudCancelacion) {
      return alert("Esta reserva tiene una solicitud de cancelación pendiente. Resuélvela antes de reprogramar.");
    }

    setLoading(true);
    try {
      const reservaRef = doc(db, "reservas", reserva.id);
      const [reservasSnap, breaksSnap, weeklySnap] = await Promise.all([
        getDocs(query(collection(db, "reservas"), where("fecha", "==", fechaNueva))),
        getDocs(query(collection(db, "breaks"), where("fecha", "==", fechaNueva))),
        getDoc(doc(db, "breaks_weekly", "default")),
      ]);

      // Compatibilidad con la selección de breaks de AdminAgendaPage.
      const breaksEspecificos = breaksSnap.docs.map((d) => d.data());
      const diaSemana = new Date(`${fechaNueva}T12:00:00`).getDay() || 7;
      const breaksSemanales = weeklySnap.data()?.defaultWeekly?.[String(diaSemana)] || [];
      const breaksAplicables = breaksEspecificos.length ? breaksEspecificos : breaksSemanales;
      const reservasDelDia = reservasSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

      validarHorarioReprogramado(reserva, fechaNueva, horaNueva, reservasDelDia, breaksAplicables);

      const idAdmin = getAuth().currentUser?.uid || null;
      const fechaOriginal = reserva.fecha;
      const horaOriginal = reserva.hora;
      const marcaHora = Timestamp.now();

      // Protege el documento si otro administrador lo modificó entretanto.
      // La verificación de ocupación se vuelve a realizar antes de guardar,
      // pero no constituye un bloqueo global frente a nuevas reservas simultáneas.
      await runTransaction(db, async (tx) => {
        const snapActual = await tx.get(reservaRef);
        if (!snapActual.exists()) throw new Error("La reserva ya no existe.");
        const actual = snapActual.data();
        if (actual.fecha !== fechaOriginal || actual.hora !== horaOriginal ||
            !["pendiente", "aprobada"].includes(actual.estado) ||
            ![null, undefined, "pendiente"].includes(actual.estadoSesion)) {
          throw new Error("La reserva cambió mientras la editabas. Actualiza la agenda e inténtalo nuevamente.");
        }

        const cambios = {
          fecha: fechaNueva,
          hora: horaNueva,
          startAt: Timestamp.fromDate(inicioNuevo),
          updatedAt: serverTimestamp(),
          ultimaReprogramacionEn: serverTimestamp(),
          recordatorio24h_enviado: false,
          "recordatorios.h24_sentAt": null,
          historialReprogramaciones: arrayUnion({
            fechaAnterior: fechaOriginal,
            horaAnterior: horaOriginal,
            fechaNueva,
            horaNueva,
            motivo: motivoReprogramacion.trim(),
            realizadoPor: idAdmin,
            realizadoEn: marcaHora,
          }),
        };
        if (actual.asistencia_confirmada === true) {
          cambios.asistencia_confirmada = false;
          cambios.asistencia_confirmada_en = deleteField();
        }
        tx.update(reservaRef, cambios);
      });

      setReserva((r) => r ? { ...r, fecha: fechaNueva, hora: horaNueva,
        startAt: Timestamp.fromDate(inicioNuevo), asistencia_confirmada: false } : r);
      setShowReprogramar(false);
      showToast("Reserva reprogramada correctamente");
      onClose?.();
      onReprogramada?.({ fechaNueva, fechaAnterior: fechaOriginal });
    } catch (err) {
      console.error("Error reprogramando reserva:", err);
      alert(err?.message || "No se pudo reprogramar la reserva.");
    } finally {
      setLoading(false);
    }
  };

  const confirmarSubModal = async () => {
    if (!reserva?.id) return;

    if (!subMotivo.trim()) {
      alert("Por favor, escribe un motivo.");
      return;
    }

    try {
      const updates = {
        updatedAt: serverTimestamp(),
        motivo_admin: subMotivo.trim(),
      };

      if (subAction === "cancelar") {
        updates.estado = "cancelada";
        updates.solicitudReprogramacion = false;
      } else {
        updates.solicitudReprogramacion = true;
      }

      await updateDoc(doc(db, "reservas", reserva.id), updates);

      if (reserva?.desdePlan && reserva?.planId) {
        try {
          await syncPlanCounters(reserva.planId);
        } catch (e) {
          console.error("Error recalculando plan:", e);
        }
      }

      setReserva((r) => (r ? { ...r, ...updates } : r));
      setShowSubModal(false);

      showToast(
        subAction === "cancelar" ? "Reserva cancelada" : "Solicitud de cambio enviada"
      );

      // ✅ SI ES PLAN Y SE CANCELA → ABRIR REEMBOLSO Y NO REFRESH AÚN
      if (reserva?.desdePlan && reserva?.planId && subAction === "cancelar") {
        setShowReembolsoModal(true);
        return;
      }

      // ✅ casos normales
      onRefresh?.();
      onClose?.();
    } catch (e) {
      console.error(e);
      alert("No se pudo actualizar la reserva.");
    }
  };


// 🔵 NUEVO: cargar planes del paciente (soporta múltiples uids + titular_uid)
// + Enriquecido con conteo real desde reservas: sesiones_usadas_calc / sesiones_reservadas_calc
const cargarPlanesDeUids = async (uids) => {
  const uniq = Array.from(new Set((uids || []).filter(Boolean)));
  if (!uniq.length) return { planes: [], uidPreferido: null };

  let planes = [];
  let uidPreferido = null;

  for (const uid of uniq) {
    const [s1, s2] = await Promise.all([
      getDocs(query(collection(db, "planes_usuarios"), where("uid", "==", uid))),
      getDocs(
        query(collection(db, "planes_usuarios"), where("titular_uid", "==", uid))
      ),
    ]);

    const arr = [...s1.docs, ...s2.docs].map((d) => ({ id: d.id, ...d.data() }));

    if (arr.length && !uidPreferido) uidPreferido = uid;
    planes.push(...arr);
  }

  // dedupe por id
  const map = new Map();
  for (const p of planes) map.set(p.id, p);
  planes = Array.from(map.values());

  // ✅ ENRIQUECER: calcular cupos reales desde "reservas"
  const planesConConteo = await Promise.all(
    planes.map(async (p) => {
      const { usadas, reservadas } = await contarSesionesPlanDesdeReservas(p.id);
      return {
        ...p,
        sesiones_usadas_calc: usadas,
        sesiones_reservadas_calc: reservadas,
      };
    })
  );

  return { planes: planesConConteo, uidPreferido: uidPreferido || uniq[0] };
};

// ===============================
// 🔎 Helpers búsqueda paciente por nombre
// ===============================
const normSearch = (t) =>
  (t || "")
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim();

const scoreMatch = (full, q) => {
  if (!q) return 999;
  if (full.startsWith(q)) return 0;
  if (full.includes(` ${q}`)) return 1;
  if (full.includes(q)) return 2;
  return 9;
};

const buscarPacientesPorNombre = async (texto) => {
  const q = normSearch(texto);
  if (q.length < 2) return [];

  // ✅ Admin: dataset normalmente chico → traemos una muestra y filtramos en frontend
  const s = await getDocs(query(collection(db, "usuarios"), limit(250)));
  const rows = s.docs.map((d) => ({ id: d.id, ...d.data() }));

  const filtrados = rows
    // opcional: evitar admins/workers si tienes ese campo
    .filter((u) => {
      const rol = (u.rol || "").toString().toLowerCase();
      return !["admin", "worker", "profesional"].includes(rol);
    })
    .map((u) => {
      const full = normSearch(`${u.nombres || ""} ${u.apellidos || ""}`);
      const email = normSearch(u.correo || u.email || "");
      return { u, full, email };
    })
    .filter(({ full, email }) => full.includes(q) || email.includes(q))
    .sort((a, b) => scoreMatch(a.full, q) - scoreMatch(b.full, q))
    .slice(0, 12)
    .map(({ u }) => u);

  return filtrados;
};


const seleccionarPaciente = async (u) => {
  setLoading(true);
  try {
    // reset plan/cupón al elegir paciente
    setPlanSeleccionado(null);
    setTipoUsoPlan("ninguno");
    setPlanesPaciente([]);
    setPacienteUid(null);
    limpiarCupon();

    // ✅ si hay rut, buscar otros docs con el mismo rut para reunir uids (como tu flujo de rut)
    let uidsCandidatos = [];
    const rutDelPaciente = u?.rut ? String(u.rut) : "";

    if (rutDelPaciente) {
      const sSameRut = await getDocs(
        query(collection(db, "usuarios"), where("rut", "==", rutDelPaciente))
      );
      uidsCandidatos = sSameRut.docs
        .map((d) => d.data()?.uid)
        .filter(Boolean);
    }

    if (!uidsCandidatos.length && u?.uid) uidsCandidatos = [u.uid];

    // ✅ Cargar planes (si hay uid)
    if (uidsCandidatos.length) {
      const { planes, uidPreferido } = await cargarPlanesDeUids(uidsCandidatos);
      setPlanesPaciente(planes);
      setPacienteUid(uidPreferido || uidsCandidatos[0] || null);
    }

    setForm((f) => ({
      ...f,
      nombres: u?.nombres || "",
      apellidos: u?.apellidos || "",
      rut: u?.rut ? formatRut(u.rut) : "",
      email: (u?.correo || u?.email || "").toString(),
    }));

    setPacienteOk(true);
    setAutofillDone(true);
    setPacienteResultados([]);
  } catch (e) {
    console.error("Error seleccionando paciente:", e);
    alert("No se pudo seleccionar el paciente.");
  } finally {
    setLoading(false);
  }
};

const handlePacienteLookup = async () => {
  const q = (pacienteQuery || "").trim();
  if (q.length < 2) {
    alert("Escribe al menos 2 caracteres del nombre.");
    return;
  }

  setLoading(true);
  try {
    const res = await buscarPacientesPorNombre(q);
    setPacienteResultados(res);

    // ✅ si solo hay 1 match → auto-seleccionar
    if (res.length === 1) {
      await seleccionarPaciente(res[0]);
      return;
    }

    // si no encuentra, igual habilitamos para llenado manual
    setPacienteOk(false);
    setAutofillDone(true);
  } finally {
    setLoading(false);
  }
};


const limpiarCupon = () => {
  setCuponInfo(null);
  setCuponMsg("");
};

const aplicarCuponManual = async () => {
  const code = (cuponCode || "").trim();
  if (!code) return;

  // ❌ Si está usando un plan existente, no corresponde cupón
  if (planSeleccionado && tipoUsoPlan !== "crear_plan") {
    setCuponInfo(null);
    setCuponMsg("❌ No se puede aplicar cupón a una reserva que viene desde un plan existente.");
    return;
  }

  if (!selectedServicio?.id) {
    setCuponInfo(null);
    setCuponMsg("❌ Selecciona un servicio antes de aplicar cupón.");
    return;
  }

  const montoBase = Number(form.monto || 0);
  if (!montoBase) {
    setCuponInfo(null);
    setCuponMsg("❌ No se pudo determinar el monto base del servicio.");
    return;
  }

  setCuponLoading(true);
  try {
    const codigoRaw = (cuponCode || "").trim();
    const codigoNorm = normKey(codigoRaw);
    const codigoUpper = codigoRaw.toUpperCase();

    let data = null;
    let docId = null;

    // 1) intentar por docId (varias variantes típicas)
    for (const idTry of [codigoNorm, codigoUpper, codigoRaw]) {
      const sTry = await getDoc(doc(db, "cupones", idTry));
      if (sTry.exists()) {
        data = sTry.data() || {};
        docId = sTry.id;
        break;
      }
    }

    // 2) fallback: buscar por campos (por si en admin se guardó con addDoc / id random)
    if (!data) {
      // si tu admin guarda "codigo_norm"
      let q1 = query(collection(db, "cupones"), where("codigo_norm", "==", codigoNorm));
      let s1 = await getDocs(q1);

      if (!s1.empty) {
        const d = s1.docs[0];
        data = d.data() || {};
        docId = d.id;
      } else {
        // si tu admin guarda "codigo" (ej: "AXELG")
        let q2 = query(collection(db, "cupones"), where("codigo", "==", codigoUpper));
        let s2 = await getDocs(q2);

        if (!s2.empty) {
          const d = s2.docs[0];
          data = d.data() || {};
          docId = d.id;
        }
      }
    }

    if (!data) {
      setCuponInfo(null);
      setCuponMsg("❌ Cupón no existe.");
      return;
    }

        console.log("CUPON variantesPermitidas:", data.variantesPermitidas);
    console.log("CUPON aplicacionVariantes:", data.aplicacionVariantes);
    console.log("VARIANTE actual:", selectedServicio?.variante);


    // === Validaciones base ===
    if (!data.activo) {
      setCuponInfo(null);
      setCuponMsg("❌ Cupón inactivo.");
      return;
    }

    // expira: puede venir Timestamp o string ISO
    const hoy = new Date();
    const expira =
      data.expira?.toDate?.() ||
      (data.expira ? new Date(data.expira) : null);

    if (expira && expira < hoy) {
      setCuponInfo(null);
      setCuponMsg("❌ Cupón expirado.");
      return;
    }

    if (typeof data.usosMax === "number" && typeof data.usos === "number") {
      if (data.usos >= data.usosMax) {
        setCuponInfo(null);
        setCuponMsg("❌ Cupón sin usos disponibles.");
        return;
      }
    }

    // === Validar servicio ===
    const aplicacionServicios = (data.aplicacionServicios || data.aplicacion || "todos")
      .toString()
      .toLowerCase();
    const pickText = (v) => {
      if (v == null) return "";
      if (typeof v === "string" || typeof v === "number") return String(v);
      if (typeof v === "object") {
        return String(
          v.value ?? v.id ?? v.token ?? v.codigo ?? v.label ?? ""
        );
      }
      return "";
    };

    const serviciosPermitidos = (Array.isArray(data.serviciosPermitidos) ? data.serviciosPermitidos : [])
      .map(pickText)
      .map((x) => x.trim())
      .filter(Boolean)
      .map(normKey);


    const servicioIdActualRaw =
      selectedServicio?.servicioDocId ||
      selectedServicio?.categoriaRegla ||
      selectedServicio?.servicioBase ||
      selectedServicio?.categoria ||
      "";

    const servicioIdActualNorm = normKey(servicioIdActualRaw);
    const tituloNorm = normKey(
      selectedServicio?.servicioBase ||
        selectedServicio?.categoriaRegla ||
        selectedServicio?.categoria ||
        ""
    );

    if (aplicacionServicios === "algunos") {
      const okServicio =
        (servicioIdActualNorm && serviciosPermitidos.includes(servicioIdActualNorm)) ||
        (tituloNorm && serviciosPermitidos.includes(tituloNorm));

      if (!okServicio) {
        setCuponInfo(null);
        setCuponMsg("❌ Este cupón no aplica a este servicio.");
        return;
      }
    }

// === Validar variante por ID real (soporta "entrenamiento:entrenamiento-mensual-2x") ===
const aplicacionVariantes = (data.aplicacionVariantes || "todas")
  .toString()
  .toLowerCase();

const varianteIdActual = String(selectedServicio?.varianteId || "").trim().toLowerCase();

// lista permitida (puede venir como strings o como objetos)
const variantesPermitidasRaw = Array.isArray(data.variantesPermitidas)
  ? data.variantesPermitidas
  : [];

const variantesPermitidasIds = variantesPermitidasRaw
  .map(pickText)
  .map((x) => String(x).trim().toLowerCase())
  .filter(Boolean)
  // ✅ si viene "entrenamiento:entrenamiento-mensual-2x" -> nos quedamos con lo último
  .map((x) => (x.includes(":") ? x.split(":").pop() : x));

if (aplicacionVariantes === "algunas") {
  if (!varianteIdActual) {
    setCuponInfo(null);
    setCuponMsg("❌ No se pudo determinar el id real de la variante para validar el cupón.");
    return;
  }

  const ok = variantesPermitidasIds.includes(varianteIdActual);

  if (!ok) {
    setCuponInfo(null);
    setCuponMsg("❌ Este cupón no aplica a esta variante.");
    return;
  }
}


    // === Calcular descuento ===
    const tipo = (data.tipoDescuento || data.tipo || "porcentaje").toString().toLowerCase();
    const valor = Number(data.valor ?? data.descuento ?? data.porcentaje ?? 0);

    let desc = 0;
    if (tipo.includes("por")) {
      desc = Math.round((montoBase * valor) / 100);
    } else {
      desc = Math.round(valor);
    }

    // cap opcional
    if (typeof data.maxDescuento === "number") {
      desc = Math.min(desc, data.maxDescuento);
    }

    const montoFinal = Math.max(0, montoBase - desc);

    setCuponInfo({
      docId,                                  // ✅ id real del doc encontrado
      codigo: (data.codigo || codigoUpper),    // ✅ código visible
      montoBase,
      descuentoMonto: desc,
      montoFinal,
      tipo,
      valor,
    });

    setCuponMsg(`✅ Cupón aplicado. Descuento: ${clp(desc)} → Total: ${clp(montoFinal)}`);
  } catch (e) {
    console.error("Error aplicando cupón:", e);
    setCuponInfo(null);
    setCuponMsg("❌ Error aplicando cupón.");
  } finally {
    setCuponLoading(false);
  }
};

// ✅ Si cambia el monto base (servicio/modalidad/plan), el cupón aplicado ya no es confiable
useEffect(() => {
  if (!cuponInfo) return;

  // Si seleccionó plan existente, no corresponde cupón
  if (planSeleccionado && tipoUsoPlan !== "crear_plan") {
    setCuponInfo(null);
    setCuponMsg("ℹ️ Se quitó el cupón porque estás usando un plan existente.");
    return;
  }

  const base = Number(form.monto || 0);
  if (base && cuponInfo.montoBase !== base) {
    setCuponInfo(null);
    setCuponMsg("ℹ️ Cambió el monto base. Reaplica el cupón.");
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [
  form.monto,
  form.modalidad,
  form.servicioId,
  planSeleccionado?.id,
  tipoUsoPlan,
]);

  const handleReservar = async () => {
  // Hora seleccionada por Paula en intervalos de 30 minutos
  const horaReserva = (manualHora || slot?.start || "").trim();

  if (!/^([01]\d|2[0-3]):(00|30)$/.test(horaReserva)) {
    alert("Selecciona una hora válida en intervalos de 30 minutos.");
    return;
  }

  if (!pacienteOk && !form.nombres?.trim()) {
    return alert(
      "Primero busca y selecciona un paciente (o escribe el nombre manualmente)."
    );
  }

  if (!form.nombres || !form.apellidos || !form.servicioId) {
    return alert("Completa nombre, apellido y servicio.");
  }

  if (!profesionalId) {
    return alert("Selecciona el profesional.");
  }

  // Aquí continúa el resto de tu función original...
    if (!pacienteOk && !form.nombres?.trim()) {
      return alert("Primero busca y selecciona un paciente (o escribe el nombre manualmente).");
    }
    if (!form.nombres || !form.apellidos || !form.servicioId)
      return alert("Completa nombre, apellido y servicio.");
    if (!profesionalId) return alert("Selecciona el profesional.");

    if (!pacienteOk && form.nombres?.trim()) setPacienteOk(true);

    setLoading(true);
    try {
  const s = serviciosSource.find((x) => x.id === form.servicioId);

  // ✅ servicio “base” para planes (sin — Mensual/Trimestral/Semestral)
  const servicio = s?.servicioBase || s?.categoria || "Servicio";

  // ✅ categoría para reglas (Kinesiología/Entrenamiento/…)
  const servicio_categoria = s?.categoriaRegla || inferCategoriaRegla(servicio);

  const servicio_variante = s?.variante || "";

  // ✅ raw bonito (para reservas)
  const servicio_raw = s?.display || `${servicio} — ${servicio_variante}`;

  const clavePrimeraSesion = getClavePrimeraSesion(servicio_categoria, servicio_variante);

let esPrimeraSesionReal = false;

if (clavePrimeraSesion) {
  if (planSeleccionado) {
    esPrimeraSesionReal =
      (planSeleccionado.sesiones_realizadas ?? 0) === 0 &&
      (planSeleccionado.sesiones_reservadas ?? 0) === 0;
  } else {
    const emailKey = (form.email || "").trim().toLowerCase();

    if (emailKey) {
      const usrSnap = await getDoc(doc(db, "usuarios", emailKey));
      const usrData = usrSnap.exists() ? usrSnap.data() : {};
      const primerasSesiones = usrData?.primerasSesiones || {};

      esPrimeraSesionReal = primerasSesiones[clavePrimeraSesion] !== true;
    }
  }
}

const norm = (t) =>
  (t || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[—–-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const catN = norm(servicio_categoria);
const varN = norm(servicio_variante);
const rawN = norm(servicio_raw);

const esKineLocal = catN.includes("kine");

const esPlanEntrenoLocal =
  (catN.includes("plan") && catN.includes("entren")) ||
  (rawN.includes("plan") && rawN.includes("entren")) ||
  (catN.includes("planes") && catN.includes("entren"));

const esParejaLocal =
  catN.includes("pareja") || varN.includes("pareja") || rawN.includes("pareja");

const esFonasaLocal =
  varN.includes("fonasa") || rawN.includes("fonasa");

let modalidadFinal = "privada";

if (esKineLocal) {
  if (esPrimeraSesionReal) {
    modalidadFinal = "privada";
  } else if (esFonasaLocal) {
    modalidadFinal = "grupal";
  } else if (planSeleccionado?.modalidad) {
    modalidadFinal = planSeleccionado.modalidad; // "privada" | "grupal"
  } else {
    modalidadFinal = form.modalidad === "privada" ? "privada" : "grupal";
  }
} else if (esPlanEntrenoLocal || catN.includes("entren")) {
  if (esParejaLocal) modalidadFinal = "privada";
  else if (esPrimeraSesionReal) modalidadFinal = "privada";
  else modalidadFinal = "grupal";
} else {
  modalidadFinal = "privada";
}

// ✔ Detectar si el servicio a reservar es "Pareja"
const servicioEsPareja = `${servicio_categoria} ${servicio_variante} ${servicio_raw}`
  .toLowerCase()
  .includes("pareja");

// ✔ Si es pareja, buscar el plan pareja y validar acompañante
const planPareja = servicioEsPareja
  ? (planesPaciente || []).find((p) => p.es_pareja)
  : null;

// 🔒 Solo exigir plan de pareja existente SI NO estamos creando un plan nuevo
if (servicioEsPareja && tipoUsoPlan !== "crear_plan") {
  if (!planPareja) {
    return alert(
      "Este servicio es 'en pareja', pero el paciente no tiene un plan de pareja activo."
    );
  }

  const parejaOk =
    planPareja?.pareja_completa === true || !!planPareja?.acompanante_info?.rut;

  if (!parejaOk) {
    return alert(
      "El plan de entrenamiento en pareja aún no tiene acompañante registrado.\n" +
        "El titular debe completar los datos antes de reservar."
    );
  }
}


// ✅ Si se está creando plan nuevo de PAREJA, exigir acompañante
if (tipoUsoPlan === "crear_plan" && esServicioPareja) {
  const rutAcomp = normalizeRut(form.pareja.rut || "");
  if (
    !form.pareja.nombres?.trim() ||
    !form.pareja.apellidos?.trim() ||
    !rutAcomp ||
    !isValidRut(rutAcomp)
  ) {
    return alert("Para crear un plan de pareja debes ingresar los datos del acompañante (incluyendo RUT válido).");
  }
}


// ✔ Metadata de pareja (se mergea luego en reservaData)
const extraPareja =
  servicioEsPareja && planPareja && tipoUsoPlan !== "crear_plan"
    ? {
        es_pareja: true,
        titular_uid: planPareja.titular_uid || planPareja.uid || null,
        acompanante_uid: planPareja.acompanante_uid || null,
        acompanante_info: planPareja.acompanante_info || null,
      }
    : {};

      const montoBase = Number(form.monto ?? s?.precio ?? precioDeServicioId(form.servicioId) ?? 0);
      const montoFinal = cuponInfo?.montoFinal ?? montoBase;
      const descuentoCupon = cuponInfo?.descuentoMonto ?? 0;
      const p = profesionales.find((x) => x.id === profesionalId);
      const nombreCompleto = `${form.nombres} ${form.apellidos}`.trim();

      // 🔵 Lógica de pago / plan
      let pagoEstado = form.pagoEstado || "pendiente";
      let extraReserva = {};
      let planPagoTipo = null;
      let monto_pagado = 0;

      // ✅ CASO: servicio suelto (NO plan) y es 1 sesión → guardar pago en la reserva
      // Esto alimenta el modal de Paciente/sesiones.jsx (pago_tipo + monto_pagado)
      if (!planSeleccionado && tipoUsoPlan !== "crear_plan" && numeroSesiones === 1) {
        if (pagoEstado === "pago50") {
          extraReserva.pago_tipo = "reserva50";
          extraReserva.monto_pagado = Math.round(Number(montoFinal || 0) * 0.5);
        } else if (pagoEstado === "pago100") {
          // OJO: en Paciente/sesiones.jsx se muestra "Pago completo" cuando pago_tipo === "completo"
          extraReserva.pago_tipo = "completo";
          extraReserva.monto_pagado = Number(montoFinal || 0);
        } else {
          extraReserva.pago_tipo = "pendiente";
          extraReserva.monto_pagado = 0;
        }
      }

      if (planSeleccionado && pacienteUid) {
        const pagadoYa = (planSeleccionado.monto_restante ?? 0) <= 0;
        let tipo = tipoUsoPlan;
        if (tipo === "ninguno") tipo = pagadoYa ? "incluida" : "valor_sesion";

        if (tipo === "valor_sesion") {
          planPagoTipo = "pago_proxima"; // pagó valor sesión
          pagoEstado = "completo";
          monto_pagado = calcularCobroPlanExistente(planSeleccionado, tipo);
        } else if (tipo === "saldo_plan") {
          planPagoTipo = "plan_saldo"; // pagó saldo del plan
          pagoEstado = "completo";
          monto_pagado = calcularCobroPlanExistente(planSeleccionado, tipo);
        } else if (tipo === "incluida") {
          planPagoTipo = "incluida"; // cliente ya pagó todo
          pagoEstado = "completo";
          monto_pagado = 0;
        }

        extraReserva = {
          ...extraReserva,
          desdePlan: true,
          planId: planSeleccionado.id,

          // ✅ fallback correcto
          precio_por_sesion: planSeleccionado.precio_por_sesion || montoBase,

          precio_total:
            planSeleccionado.precio_total ||
            (planSeleccionado.precio_por_sesion || montoBase) *
              (planSeleccionado.sesiones_totales || 1),

          pago_tipo: planPagoTipo,
          monto_pagado,
        };
      }

let nuevoPlanId = null;
let planCreadoMeta = null; // ✅ para usar en reservaData

// ✅ Crear plan SOLO si corresponde (MANUAL ADMIN) — con mismos campos que agenda.jsx
if (tipoUsoPlan === "crear_plan" && pacienteUid) {
  const sesionesTotales = Math.max(1, Number(numeroSesiones || 1));

  const montoBasePlan = Number(form.monto || 0);
  const montoTotalPlan = cuponInfo?.montoFinal ?? montoBasePlan;
  const descuentoPlan = cuponInfo?.descuentoMonto ?? 0;
  const valorSesionPlan = Math.round(montoTotalPlan / sesionesTotales);

  const pagoInicialPlan = PRECIO_PRIMERA_SESION_PLAN;

  const pagadoInicial =
    form.pagoEstado === "plan_pago_completo"
      ? montoTotalPlan
      : form.pagoEstado === "plan_reserva"
      ? Math.min(pagoInicialPlan, montoTotalPlan)
      : 0;

  const montoRestantePlan =
    form.pagoEstado === "plan_pago_completo"
      ? 0
      : form.pagoEstado === "plan_reserva"
      ? Math.max(0, montoTotalPlan - pagadoInicial)
      : montoTotalPlan;

    const servicioLabelPlan = servicio; // ✅ base: "Planes de Entrenamiento"
    const servicioRawPlan = `${servicioLabelPlan} - ${servicio_variante}`;

  const esParejaNuevoPlan = esServicioPareja; // tu useMemo

  const acompInfo = esParejaNuevoPlan
    ? {
        nombre: (form.pareja.nombres || "").trim(),
        apellido: (form.pareja.apellidos || "").trim(),
        rut: normalizeRut(form.pareja.rut || ""),
        email: (form.pareja.email || "").trim().toLowerCase(),
        telefono: (form.pareja.telefono || "").trim(),
      }
    : null;

    // Guardar la modalidad realmente seleccionada para el plan
    let modalidadPlan = form.modalidad || null;


  planCreadoMeta = {
  sesionesTotales,
  montoTotalPlan,
  valorSesionPlan,
  pagadoInicial,
  montoRestantePlan,
  descuentoPlan,
  codigoCupon: cuponInfo?.codigo || null,
};


  const planDoc = {
    uid: pacienteUid,
    titular_uid: pacienteUid,

    // ✅ CAMPOS NUEVOS (igual que agenda.jsx)
    servicio: servicioLabelPlan,
    servicio_categoria: servicio_categoria,
    servicio_variante: servicio_variante,
    servicio_raw: servicioRawPlan,
    modalidad: modalidadPlan,

    // ✅ Compatibilidad con lo que ya tienes armado (tu matcher usa categoria/variante)
    categoria: servicioLabelPlan,
    variante: servicio_variante,

    sesiones_totales: sesionesTotales,
    sesiones_usadas: 0,
    sesiones_reservadas: 1,

    // (opcional pero recomendado para evitar “undefined” en lógica antigua)
    sesiones_realizadas: 0,

    precio_total: montoTotalPlan,
    precio_por_sesion:
      sesionesTotales > 1
        ? calcularPrecioSesionPosteriorPlan(montoTotalPlan, sesionesTotales)
        : montoTotalPlan,

    pago_total_realizado: pagadoInicial,
    monto_restante: montoRestantePlan,

    creadoEn: serverTimestamp(),
    updatedAt: serverTimestamp(),

    monto_original: montoBasePlan,
    cupon_codigo: cuponInfo?.codigo || null,
    cupon_descuento: descuentoPlan,

    ...(esParejaNuevoPlan
      ? {
          es_pareja: true,
          acompanante_uid: null,
          acompanante_info: acompInfo,
          pareja_completa: true, // en admin ya los ingresaste
        }
      : {
          es_pareja: false,
          pareja_completa: true,
        }),
  };

  const planRef = await addDoc(collection(db, "planes_usuarios"), planDoc);
  nuevoPlanId = planRef.id;
}


const startDate = toDateFromYmdHm(slot.dateISO, horaReserva);
const startAt = Timestamp.fromDate(startDate);


      const reservaData = {
        fecha: slot.dateISO,
        hora: horaReserva,
        startAt,
        estado: "aprobada",
        estadoSesion: "pendiente",
        nombre: nombreCompleto,
        uid: pacienteUid || null,
        titular_uid: pacienteUid || null,
        rut: form.rut || "",
        email: form.email || "",
        servicio_raw,
        servicio_categoria,
        servicio_variante,
        monto: montoFinal,
        monto_original: montoBase,
        cupon_codigo: cuponInfo?.codigo || null,
        cupon_descuento: descuentoCupon,
        pagoEstado,
        notas: form.notas || "",
        profesionalId,
        profesionalNombre: p?.nombre || "—",
        origen: "manual_admin",
        reservaManualSinRestricciones: true,
        creadoEn: serverTimestamp(),
        updatedAt: serverTimestamp(),
        ...extraReserva,
        ...extraPareja,
        ...(nuevoPlanId
          ? {
              desdePlan: true,
              planId: nuevoPlanId,

              // ✅ coherente con el plan creado (con cupón si existía)
              precio_por_sesion: planCreadoMeta?.valorSesionPlan ?? (montoBase / numeroSesiones),
              precio_total: planCreadoMeta?.montoTotalPlan ?? montoBase,

                pago_tipo:
                  form.pagoEstado === "plan_pago_completo"
                    ? "completo"
                    : form.pagoEstado === "plan_reserva"
                    ? "pago_inicial_plan"
                    : "incluida",

              monto_pagado:
                planCreadoMeta?.pagadoInicial ??
                (form.pagoEstado === "plan_pago_completo"
                  ? (planCreadoMeta?.montoTotalPlan ?? form.monto)
                  : form.pagoEstado === "plan_reserva"
                  ? 15250
                  : 0),

              // (opcional, pero útil)
              monto_restante: planCreadoMeta?.montoRestantePlan ?? 0,

              ...(tipoUsoPlan === "crear_plan" && esServicioPareja
                ? {
                    es_pareja: true,
                    titular_uid: pacienteUid,
                    acompanante_uid: null,
                    acompanante_info: {
                      nombre: (form.pareja.nombres || "").trim(),
                      apellido: (form.pareja.apellidos || "").trim(),
                      rut: normalizeRut(form.pareja.rut || ""),
                      email: (form.pareja.email || "").trim().toLowerCase(),
                      telefono: (form.pareja.telefono || "").trim(),
                    },
                  }
                : {}),
            }
          : {}),
        modalidad: modalidadFinal,
      };

      const ref = await addDoc(collection(db, "reservas"), reservaData);

      if (clavePrimeraSesion && esPrimeraSesionReal && form.email) {
      await setDoc(
        doc(db, "usuarios", form.email.trim().toLowerCase()),
        {
          primerasSesiones: {
            [clavePrimeraSesion]: true,
          },
        },
        { merge: true }
      );
    }

      await updateDoc(doc(db, "reservas", ref.id), {
        modalidad: modalidadFinal,
        updatedAt: serverTimestamp(),
      });

      // 🔵 asegurar profesionalId en la colección usuarios
      if (form.email) {
        const refUsuario = doc(db, "usuarios", form.email);
        await setDoc(
          refUsuario,
          {
            profesionalId,
            profesionalAsignadoEn: serverTimestamp(),
          },
          { merge: true }
        );
      }


      // 🔵 Si se usó plan, actualizar plan + movimiento de pago
      if (planSeleccionado && pacienteUid) {
        try {
          const planRef = doc(db, "planes_usuarios", planSeleccionado.id);

          const planUpdates = {
            sesiones_reservadas: increment(1),
          };

          if (planPagoTipo === "pago_proxima" && monto_pagado) {
            planUpdates.pago_total_realizado = increment(monto_pagado);
            planUpdates.monto_restante = increment(-monto_pagado);
          } else if (planPagoTipo === "plan_saldo" && monto_pagado) {
            planUpdates.pago_total_realizado = increment(monto_pagado);
            planUpdates.monto_restante = 0;
          }

          await updateDoc(planRef, planUpdates);

          if (
            (planPagoTipo === "pago_proxima" || planPagoTipo === "plan_saldo") &&
            monto_pagado
          ) {
            await addDoc(collection(db, "planes_movimientos"), {
              planId: planSeleccionado.id,
              reservaId: ref.id,
              tipo: "pago",
              monto: Math.abs(monto_pagado),
              motivo:
                planPagoTipo === "pago_proxima"
                  ? "Pago valor sesión registrado desde SlotModal"
                  : "Pago saldo restante de plan registrado desde SlotModal",
              fecha: serverTimestamp(),
            });
          }
        } catch (e) {
          console.error(
            "Error actualizando plan tras crear reserva manual desde SlotModal:",
            e
          );
        }
      }

      showToast("Reserva creada y aprobada correctamente");
      onClose?.();
      onRefresh?.();
    } catch (e) {
      console.error(e);
      alert("No se pudo crear la reserva.");
    } finally {
      setLoading(false);
    }
  };



  /* =========================
      Derivados para el render
  ========================= */
  const estado = reserva?.estado || (mode === "new" ? "new" : "");
  const estadoSesionHumano =
    reserva?.estadoSesion === "completada"
      ? "Completada (paciente asistió)"
      : reserva?.estadoSesion === "realizada"
      ? "Realizada (paciente no asistió)"
      : reserva?.estadoSesion === "pendiente"
      ? "Pendiente"
      : reserva?.estadoSesion === "cancelada"
      ? "Cancelada"
      : reserva?.estadoSesion || "—";

const selectedServicio =
  form.servicioId && serviciosSource
    ? serviciosSource.find((x) => x.id === form.servicioId)
    : null;

// Resumen administrativo: precio de esta RESERVA, no del plan completo.
const cobroPlanExistente = planSeleccionado
  ? calcularCobroPlanExistente(planSeleccionado, tipoUsoPlan)
  : null;

// ✅ Monto real a cobrar (si hay cupón, manda el total con descuento)
const montoEfectivo = useMemo(() => {
  const base = Number(form.monto || 0);
  const total = Number(cuponInfo?.montoFinal ?? base);
  return total;
}, [form.monto, cuponInfo?.montoFinal]);


const esKine = useMemo(() => {
  const cat = (selectedServicio?.categoria || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
  return cat.includes("kinesiolog"); // robusto con/sin acento
}, [selectedServicio?.categoria]);

const tieneModalidadPrecio = useMemo(() => {
  if (!selectedServicio) return false;
  return (
    typeof selectedServicio.precio_grupo === "number" ||
    typeof selectedServicio.precio_privada === "number"
  );
}, [
  selectedServicio?.id,
  selectedServicio?.precio_grupo,
  selectedServicio?.precio_privada,
]);


    const esServicioPareja = useMemo(() => {
  if (!selectedServicio) return false;

  const raw = `${selectedServicio.categoria} ${selectedServicio.variante} ${selectedServicio.raw || ""}`
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

  return raw.includes("pareja");
}, [selectedServicio]);


const inferSesionesTotalesDesdeVariante = (txt) => {
  const t = (txt || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim();

  // Caso: "2 veces x sem (mensual)" / "3 veces x sem (trimestral)" etc
  const m = t.match(/(\d+)\s*veces?\s*x\s*sem/);
  if (m) {
    const vecesSemana = parseInt(m[1], 10) || 1;

    let semanas = 4; // mensual default
    if (t.includes("trimestral")) semanas = 12;
    else if (t.includes("semestral")) semanas = 24;
    else if (t.includes("mensual")) semanas = 4;

    return vecesSemana * semanas;
  }

  // Caso: "10 sesiones"
  const ms = t.match(/(\d+)\s*sesion/);
  if (ms) return parseInt(ms[1], 10) || 1;

  // Fallback: primer número
  const any = t.match(/\d+/);
  return any ? parseInt(any[0], 10) : 1;
};

// SOLO PARA CONTRATACIONES NUEVAS. El progreso de planes activos NO se recalcula.
const numeroSesiones =
  obtenerSesionesEntrenamiento1x(
    selectedServicio?.categoria,
    selectedServicio?.varianteId
  ) ?? inferSesionesTotalesDesdeVariante(selectedServicio?.variante);

const sesionesTotalesPlan = (p) => {
  const st = Number(p?.sesiones_totales || 0);
  const inferred = inferSesionesTotalesDesdeVariante(p?.variante);

  // Si viene bugueado (muy bajo), usamos el inferred
  if (inferred && st && st < inferred) return inferred;

  return st || inferred || 0;
};



const planesCompatibles = useMemo(() => {
  if (!selectedServicio) return [];
  // Los planes antiguos y los actuales guardan nombre de categoría/variante
  // en formatos distintos. Validar también periodo, frecuencia y pareja.
  return (planesPaciente || []).filter((p) => coincidePlanConServicio(p, selectedServicio));
}, [planesPaciente, selectedServicio]);

const planesUsables = useMemo(() => {
  return planesCompatibles.filter((p) => {
    const usadas = Number(
      p?.sesiones_usadas_calc ?? p?.sesiones_usadas ?? 0
    );

    const reservadas = Number(
      p?.sesiones_reservadas_calc ?? p?.sesiones_reservadas ?? 0
    );

    const totales = sesionesTotalesPlan(p);

    // ✅ Compatibilidad estado / estado_plan
    const estado = (
      p?.estado_plan ||
      p?.estado ||
      "activo"
    )
      .toString()
      .toLowerCase();

    const activo =
      ![
        "cancelado",
        "cancelada",
        "anulado",
        "anulada",
        "finalizado",
        "finalizada",
        "vencido",
        "vencida",
        "inactivo",
      ].includes(estado);

    const tieneCupos = usadas + reservadas < totales;

    return activo && tieneCupos;
  });
}, [planesCompatibles]);

const mostrarCrearPlanNuevo = useMemo(
  () => planesUsables.length === 0 && numeroSesiones > 1,
  [planesUsables.length, numeroSesiones]
);

// auto-selección estable
useEffect(() => {
  if (!selectedServicio) return;

  // ✅ Si el usuario eligió crear plan, NO lo pises
  if (tipoUsoPlan === "crear_plan") return;

  // ✅ Si ya hay un plan seleccionado y sigue siendo usable, NO lo pises
  if (planSeleccionado?.id && planesUsables.some((p) => p.id === planSeleccionado.id)) {
    return;
  }

  if (planesUsables.length === 1) {
    const pSel = planesUsables[0];

    if (planSeleccionado?.id !== pSel.id) setPlanSeleccionado(pSel);

    const pagado = (pSel.monto_restante ?? 0) <= 0;
    const nextTipo = pagado ? "incluida" : "valor_sesion";
    if (tipoUsoPlan !== nextTipo) setTipoUsoPlan(nextTipo);
  } else {
    if (planSeleccionado) setPlanSeleccionado(null);
    if (tipoUsoPlan !== "ninguno") setTipoUsoPlan("ninguno");
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [selectedServicio?.id, planesUsables.length, planesUsables?.[0]?.id, tipoUsoPlan, planSeleccionado?.id]);

// ✅ Si hay plan seleccionado (kine) y trae modalidad -> forzar UI + monto
useEffect(() => {
  if (!selectedServicio) return;

  // solo kinesiología con precios por modalidad
  if (!esKine || !tieneModalidadPrecio) return;

  // si estás creando plan nuevo, la modalidad se elige manualmente
  if (tipoUsoPlan === "crear_plan") return;

  // si hay plan seleccionado y tiene modalidad -> manda
  const mPlan = planSeleccionado?.modalidad; // "privada" | "grupal" (DB)
  if (!mPlan) return;

  const modalidadUI = mPlan === "grupal" ? "grupal" : "privada";

  // evitar loops
  if (form.modalidad === modalidadUI) return;

  setForm((f) => ({
    ...f,
    modalidad: modalidadUI,
    monto: obtenerPrecioKinesiologia(selectedServicio, modalidadUI),
  }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [
  selectedServicio?.id,
  esKine,
  tieneModalidadPrecio,
  tipoUsoPlan,
  planSeleccionado?.id,
  planSeleccionado?.modalidad,
]);

useEffect(() => {
  if (!selectedServicio) return;
  if (!mostrarCrearPlanNuevo) return;
  if (tipoUsoPlan === "crear_plan") return; // ya está seleccionado

  // Auto-seleccionar "crear plan" si no hay planes usables y la variante es un plan
  setTipoUsoPlan("crear_plan");
}, [selectedServicio?.id, mostrarCrearPlanNuevo]);

  const esPlan = reserva?.desdePlan === true;
  const puedeMarcarPagoCompleto =
    reserva &&
    ((!esPlan &&
      (reserva.pago_tipo === "reserva50" || reserva.pagoEstado === "parcial")) ||
      (esPlan && (reserva.monto_restante ?? 0) > 0));

const esParejaReserva = !!(
  planInfo?.es_pareja ||
  reserva?.requiereSegundoParticipante ||
  reserva?.es_pareja ||
  reserva?.esPareja
);

const acompananteInfo = planInfo?.acompanante_info || null;

const fechaMostrada =
  mode === "detail"
    ? reserva?.fecha || slot?.dateISO
    : slot?.dateISO;

const horaMostrada =
  mode === "detail"
    ? reserva?.hora || slot?.start
    : manualHora || slot?.start;


  /* =========================
      Render principal
  ========================= */
  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-[15000] flex items-center justify-center p-4">
        <div
          className="absolute inset-0 bg-black/40 backdrop-blur-sm"
          onClick={onClose}
        />
        <div className="absolute inset-0 flex items-center justify-center p-4">
          <div className="relative w-full max-w-2xl rounded-2xl bg-white shadow-2xl ring-1 ring-zinc-100 z-[10000]">
            {/* ===== Header ===== */}
            <div className="border-b border-zinc-100 p-5 relative">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-xl font-semibold tracking-tight">
                    {mode === "detail" ? "Detalle de reserva" : "Agendar manualmente"}
                  </h3>
                    <p className="text-sm text-zinc-500">
                      {fechaMostrada} · {horaMostrada}
                    </p>
                </div>

                <div className="flex items-center gap-2 relative z-[60]">
                  {mode === "detail" &&
                    !readOnly &&
                    ["pendiente", "aprobada"].includes(estado) &&
                    !["completada", "realizada", "realizada_asiste", "realizada_no_asiste"].includes(reserva?.estadoSesion) && (
                      <div className="relative" ref={dropdownRef}>
                        <button
                          onClick={() => setShowOpciones((s) => !s)}
                          className="inline-flex items-center justify-center h-9 w-9 rounded-full border border-zinc-300 hover:bg-zinc-100 transition"
                          title="Opciones"
                        >
                          <MoreVertical className="h-5 w-5" />
                        </button>

                        {showOpciones && (
                          <div className="absolute right-0 mt-2 w-64 rounded-xl border border-zinc-200 bg-white shadow-lg overflow-hidden z-[100] animate-fadeIn">
                            <button
                              type="button"
                              onClick={() => {
                                setShowOpciones(false);
                                setNuevaFechaReserva(reserva?.fecha || slot?.dateISO || "");
                                setNuevaHoraReserva(reserva?.hora || slot?.start || "");
                                setMotivoReprogramacion("");
                                setShowReprogramar(true);
                              }}
                              className="flex items-center gap-2 w-full text-left px-4 py-3 hover:bg-blue-50 text-sm text-blue-800"
                            >
                              <CalendarClock className="h-4 w-4" /> Reprogramar reserva
                            </button>
                            <div className="border-t border-zinc-100" />
                            <button
                              onClick={() => {
                                setShowOpciones(false);
                                setSubAction("cancelar");
                                setShowSubModal(true);
                              }}
                              className="flex items-center gap-2 w-full text-left px-4 py-3 hover:bg-zinc-50 text-sm text-zinc-700"
                            >
                              ❌ Cancelar reserva
                            </button>
                          </div>
                        )}
                      </div>
                    )}

                  <button
                    onClick={onClose}
                    className="inline-flex items-center justify-center h-9 w-9 rounded-full hover:bg-zinc-100 transition"
                    title="Cerrar"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>

            {/* ===== Body ===== */}
            <div className="max-h-[72vh] overflow-y-auto p-5 space-y-4 [scrollbar-width:thin] [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-zinc-300/80 [&::-webkit-scrollbar-track]:bg-transparent">
              {mode === "detail" ? (
                <div className="space-y-5">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <Info label="Paciente" value={reserva?.nombre || "—"} />
                    <Info label="Email" value={reserva?.email || "—"} />
                    <Info
                      label="Servicio"
                      value={
                        reserva?.servicio_raw ||
                        [reserva?.servicio_categoria, reserva?.servicio_variante]
                          .filter(Boolean)
                          .join(" — ") ||
                        "—"
                      }
                    />
                    {reserva?.modalidad && (
                      <Info
                        label="Modalidad"
                        value={
                          (reserva?.modalidad === "privada" && "Sesión privada") ||
                          (reserva?.modalidad === "grupal" && "Sesión en grupo") ||
                          "—"
                        }
                      />
                    )}
                    <Info
                      label="Monto"
                      value={
                        reserva?.monto
                          ? `$${reserva.monto.toLocaleString("es-CL")}`
                          : reserva?.precio_total
                          ? `$${reserva.precio_total.toLocaleString("es-CL")}`
                          : reserva?.monto_pagado
                          ? `$${reserva.monto_pagado.toLocaleString("es-CL")}`
                          : "—"
                      }
                    />
                    <Info label="Estado" value={reserva?.estado || "—"} />
                      {solicitudCancelacion && (
                          <div className="col-span-2 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm">
                            <div className="font-semibold text-rose-700 mb-2">
                              Solicitud de cancelación pendiente
                            </div>
                            <p className="text-sm text-rose-800">
                              <strong>Motivo del paciente:</strong><br />
                              {solicitudCancelacion.motivo || "—"}
                            </p>
                          </div>
                      )}
                        {solicitudCancelacion && (
                          <div className="col-span-2 mt-3">
                            <label className="text-sm font-medium text-zinc-700 mb-1 block">
                              Respuesta de la kinesióloga
                            </label>

                            <textarea
                              value={respuestaAdmin}
                              onChange={(e) => setRespuestaAdmin(e.target.value)}
                              rows={3}
                              placeholder="Escribe tu respuesta para el paciente…"
                              className="w-full border border-zinc-300 rounded-lg px-3 py-2 text-sm"
                            />
                          </div>
                        )}
                    <Info label="Estado sesión" value={estadoSesionHumano} />
                    {/* ✅ CUPÓN (si aplica) */}
                    {reserva?.cupon_codigo && (
                      <div className="col-span-2 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm">
                        <div className="font-semibold text-emerald-800 mb-1">Cupón aplicado</div>

                        <div className="grid grid-cols-2 gap-2">
                          <div className="text-emerald-900">
                            <span className="text-emerald-700">Código:</span>{" "}
                            <strong>{String(reserva.cupon_codigo).toUpperCase()}</strong>
                          </div>

                          <div className="text-emerald-900">
                            <span className="text-emerald-700">Descuento:</span>{" "}
                            <strong>-{clp(Number(reserva.cupon_descuento || 0))}</strong>
                          </div>

                          {typeof reserva?.monto_original === "number" && (
                            <div className="text-emerald-900 col-span-2">
                              <span className="text-emerald-700">Monto base:</span>{" "}
                              <strong>{clp(reserva.monto_original)}</strong>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    {reserva?.asistencia_confirmada && (
                      <div className="col-span-2 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm flex items-start gap-3">
                        <CheckCircle2 className="h-5 w-5 text-emerald-600 mt-0.5" />
                        <div>
                          <p className="font-semibold text-emerald-800">
                            Asistencia confirmada por el paciente
                          </p>
                          {reserva?.asistencia_confirmada_en && (
                            <p className="text-emerald-700 text-xs mt-1">
                              Confirmada el{" "}
                              {reserva.asistencia_confirmada_en.toDate
                                ? reserva.asistencia_confirmada_en
                                    .toDate()
                                    .toLocaleString("es-CL")
                                : ""}
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                    {!reserva?.asistencia_confirmada &&
                      reserva?.estado === "aprobada" &&
                      reserva?.estadoSesion === "pendiente" && (
                        <div className="col-span-2 rounded-xl border border-zinc-200 bg-zinc-50 p-3 text-sm text-zinc-600">
                          El paciente aún no ha confirmado su asistencia.
                        </div>
                    )}
                    {reserva?.profesionalNombre && (
                      <Info label="Profesional asignado" value={reserva.profesionalNombre} />
                    )}
                  </div>

                  {/* ==== INFO PLAN PAREJA (solo si aplica) ==== */}

                  {esParejaReserva && (
                    <div className="rounded-xl border border-purple-200 bg-purple-50 p-4 text-sm mb-3">
                      <div className="font-semibold text-purple-900 text-base mb-2">
                        🧑‍🤝‍🧑 Entrenamiento en Pareja
                      </div>

                      <div>
                        <p className="font-semibold text-purple-800 mb-1">Acompañante</p>

                        {acompananteInfo ? (
                          <>
                            <p className="text-purple-900">
                              <strong>Nombre:</strong>{" "}
                              {`${acompananteInfo.nombre || ""} ${acompananteInfo.apellido || ""}`.trim()}
                            </p>

                            <p className="text-purple-900">
                              <strong>Email:</strong> {acompananteInfo.email || "—"}
                            </p>

                            {acompananteInfo.rut && (
                              <p className="text-purple-900">
                                <strong>RUT:</strong> {acompananteInfo.rut}
                              </p>
                            )}

                            {acompananteInfo.telefono && (
                              <p className="text-purple-900">
                                <strong>Teléfono:</strong> {acompananteInfo.telefono}
                              </p>
                            )}
                          </>
                        ) : (
                          <p className="text-purple-900 italic">Acompañante aún no registrado.</p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Pago + info plan (detalle) */}
                  <div className="rounded-xl border border-zinc-200 bg-zinc-50 p-3 text-sm">
                    <div className="font-semibold text-zinc-700 mb-1">Pago</div>

                    {(() => {
                      const metodoPagoFinal =
                        reserva?.pago_metodo ||
                        pago?.metodo ||
                        (reserva?.pago_tipo === "incluida" ? "incluida" : "—");

                      const pagoTipoHumano =
                        {
                          completo: "Pago completo",
                          pago100: "Pago completo",
                          reserva50: "Reserva 50%",
                          pago_proxima: "Pago próxima sesión",
                          pago_total_restante: "Pago saldo restante",
                          plan_saldo: "Pago saldo restante",
                          incluida: "Incluida en el plan",
                        }[reserva?.pago_tipo] || reserva?.pago_tipo || "—";

                      const esPlanLocal = reserva?.desdePlan === true;

                      return (
                        <div className="space-y-2">
                          <div>
                            <span className="text-zinc-500">Estado:</span>{" "}
                            {reserva?.pago_tipo === "incluida"
                              ? "Incluida en el plan"
                              : pagoTipoHumano}
                          </div>

                          <div>
                            <span className="text-zinc-500">Método:</span>{" "}
                            {metodoPagoFinal}
                          </div>

                          {esPlanLocal && (
                            <div className="mt-2 p-3 rounded-xl bg-blue-50 border border-blue-100">
                              <div className="font-semibold text-blue-800 mb-2">
                                Sesión de plan
                              </div>

                              {!planInfo ? (
                                <p className="text-sm text-blue-900">
                                  Cargando información del plan…
                                </p>
                              ) : (
                                <>
                                  <p className="text-sm text-blue-900">
                                    <strong>Sesión:</strong>{" "}
                                    {planInfo.sesiones_usadas + 1} de{" "}
                                    {planInfo.sesiones_totales}
                                  </p>

                                  <p className="text-sm text-blue-900">
                                    <strong>Valor por sesión:</strong>{" "}
                                    {clp(planInfo.precio_por_sesion)}
                                  </p>

                                  <p className="text-sm text-blue-900">
                                    <strong>Total pagado del plan:</strong>{" "}
                                    {clp(planInfo.pago_total_realizado)}
                                  </p>

                                  <p className="text-sm text-blue-900">
                                    <strong>Saldo restante del plan:</strong>{" "}
                                    {planInfo.monto_restante > 0 ? (
                                      <span className="text-rose-700 font-semibold">
                                        {clp(planInfo.monto_restante)}
                                      </span>
                                    ) : (
                                      <span className="text-emerald-700 font-semibold">
                                        Pagado ✓
                                      </span>
                                    )}
                                  </p>

                                  <p className="text-sm text-blue-900 mt-1">
                                    <strong>Pago tipo:</strong> {pagoTipoHumano}
                                  </p>

                                  <p className="text-sm text-blue-900">
                                    <strong>Monto pagado por esta sesión:</strong>{" "}
                                    {clp(reserva?.monto_pagado ?? 0)}
                                  </p>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })()}

                    {/* Comprobantes */}
                    {Array.isArray(pago?.comprobantes) && pago.comprobantes.length > 0 && (
                      <div className="mt-3">
                        <p className="text-zinc-600 font-medium mb-2">Comprobantes:</p>
                        <div className="flex gap-3 flex-wrap">
                          {pago.comprobantes.map((url, i) => (
                            <button
                              key={i}
                              onClick={() => setZoomed(url)}
                              className="relative h-24 w-24 overflow-hidden rounded-lg ring-1 ring-zinc-200 hover:scale-[1.02] transition"
                              title="Ver comprobante"
                            >
                              <img
                                src={url}
                                alt={`Comprobante ${i + 1}`}
                                className="h-full w-full object-cover"
                              />
                              <ZoomIn className="absolute bottom-1 right-1 h-4 w-4 text-white/90 drop-shadow" />
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {(pago?.comprobante_url ||
                      pago?.comprobanteURL ||
                      reserva?.comprobante_url ||
                      reserva?.comprobanteURL) && (
                      <div className="mt-2">
                        <button
                          onClick={() =>
                            setZoomed(
                              pago?.comprobante_url ||
                                pago?.comprobanteURL ||
                                reserva?.comprobante_url ||
                                reserva?.comprobanteURL
                            )
                          }
                          className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-zinc-200 hover:bg-zinc-300 transition"
                        >
                          <ZoomIn className="h-4 w-4" />
                          Ver comprobante
                        </button>
                      </div>
                    )}

                    {pago && pago?.estado !== "aprobado" && (
                      <div className="pt-3">
                        <button
                          onClick={handleApprovePago}
                          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition"
                        >
                          <CheckCircle2 className="h-4 w-4" />
                          Aprobar pago
                        </button>
                      </div>
                    )}

                    {puedeMarcarPagoCompleto && (
                      <div className="pt-3">
                        <button
                          onClick={handleMarcarPagoCompleto}
                          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition"
                        >
                          <CheckCircle2 className="h-4 w-4" />
                          Marcar pago completo
                        </button>
                      </div>
                    )}
                  </div>

                  {solicitudCancelacion && (
                    <div className="mt-4 space-y-3">

                      <button
                        onClick={handleAprobarCancelacionSinReembolso}
                        className="w-full px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700"
                      >
                        Aprobar cancelación (sin reembolso)
                      </button>

                      <button
                        onClick={() => setShowReembolsoUI(true)}
                        className="w-full px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
                      >
                        Aprobar con reembolso
                      </button>

                      <button
                        onClick={handleRechazarCancelacion}
                        className="w-full px-4 py-2 rounded-lg bg-rose-600 text-white hover:bg-rose-700"
                      >
                        Rechazar solicitud
                      </button>

                    </div>
                  )}

                  {/* Botonera dinámica */}
                    <div className="pt-4 border-t border-zinc-100">

                      {/* === ESTADO PENDIENTE === */}
                      {estado === "pendiente" && (
                         !solicitudCancelacion && (
                        <>
                          {/* Selector de profesional */}
                          <div className="flex flex-col sm:flex-row sm:items-end gap-3 sm:gap-4">
                            <div className="flex-1">
                              <label className="text-sm text-zinc-600 font-medium block mb-1">
                                Profesional que atenderá
                              </label>

                              <select
                                isSearchable={false}
                                value={profesionalId}
                                onChange={(e) => setProfesionalId(e.target.value)}
                                className="w-full border border-zinc-300 rounded-md px-3 py-2 bg-white"
                              >
                                <option value="">Seleccione profesional…</option>
                                {profesionales.map((p) => (
                                  <option key={p.id} value={p.id}>
                                    {p.nombre}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>

                          {/* Botones Aprobar / Rechazar */}
                          <div className="mt-4 flex gap-3">
                            <button
                              onClick={handleAprobar}
                              className="px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition"
                            >
                              Aprobar reserva
                            </button>

                            <button
                              onClick={handleRechazar}
                              className="px-4 py-2 rounded-lg bg-rose-600 text-white hover:bg-rose-700 transition"
                            >
                              Rechazar reserva
                            </button>
                          </div>
                        </>
                          ) 
                      )}

                      {/* === ESTADO APROBADA === */}
                      {estado === "aprobada" && (
                        !solicitudCancelacion ? (
                        <>
                          {/* Selector de profesional */}
                          <div className="flex flex-col sm:flex-row sm:items-end gap-3 sm:gap-4">
                            <div className="flex-1">
                              <label className="text-sm text-zinc-600 font-medium block mb-1">
                                Profesional que atenderá
                              </label>

                              <select
                                isSearchable={false}
                                value={profesionalId}
                                onChange={(e) => setProfesionalId(e.target.value)}
                                className="w-full border border-zinc-300 rounded-md px-3 py-2 bg-white"
                              >
                                <option value="">Seleccione profesional…</option>
                                {profesionales.map((p) => (
                                  <option key={p.id} value={p.id}>
                                    {p.nombre}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>

                          {/* Botón para guardar profesional */}
                          <div className="mt-4">
                            <button
                              onClick={async () => {
                                if (!reserva?.id) return;

                                const profesional = profesionales.find((p) => p.id === profesionalId);

                                await updateDoc(doc(db, "reservas", reserva.id), {
                                  profesionalId: profesionalId || "",
                                  profesionalNombre: profesional?.nombre || "",
                                  updatedAt: serverTimestamp(),
                                });

                                showToast("Profesional actualizado");
                                onRefresh?.();
                              }}
                              className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition"
                            >
                              Guardar profesional
                            </button>
                          </div>
                        </>
                         ) : null
                      )}

                      {/* === DROPDOWN DE SESIÓN (NO SE TOCA) === */}
                      {reserva?.estado === "aprobada" && !solicitudCancelacion && (
                        <div className="flex items-center justify-between mt-3">
                          {/* ESTE BLOQUE QUEDA INTACTO */}
                          <div className="relative z-[200]">
                            <button
                              onClick={() => setShowSesionDropdown((s) => !s)}
                              className="px-4 py-2 rounded-lg bg-[#0a2540] text-white hover:bg-[#123366] transition shadow-sm"
                            >
                              Actualizar estado de sesión
                            </button>
                          {showSesionDropdown && (
                            <div
                              ref={sesionDropdownRef}
                              className="fixed inset-0 z-[25000] flex items-center justify-center"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="absolute inset-0 bg-black/20 pointer-events-none" />
                              <div className="bg-white rounded-2xl shadow-2xl border border-zinc-200 w-[320px] p-2 animate-fadeIn ring-1 ring-zinc-100 relative z-[300]">
                                <p className="text-sm font-semibold text-zinc-700 px-3 pt-2 pb-1">
                                  Selecciona un nuevo estado:
                                </p>

                                <button
                                  onClick={() => handleSesionEstado("completada")}
                                  className="flex w-full items-center gap-3 text-left px-3 py-2.5 hover:bg-emerald-50 rounded-lg transition"
                                >
                                  <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                                  <span className="text-sm text-zinc-800">
                                    Completada (paciente asistió)
                                  </span>
                                </button>

                                <button
                                  onClick={() => handleSesionEstado("realizada")}
                                  className="flex w-full items-center gap-3 text-left px-3 py-2.5 hover:bg-rose-50 rounded-lg transition"
                                >
                                  <AlertTriangle className="h-5 w-5 text-rose-600" />
                                  <span className="text-sm text-zinc-800">
                                    Realizada (paciente no asistió)
                                  </span>
                                </button>

                                <div className="flex justify-end mt-2 border-t border-zinc-100 pt-2 px-3">
                                  <button
                                    onClick={() => setShowSesionDropdown(false)}
                                    className="text-sm text-zinc-600 hover:text-zinc-800"
                                  >
                                    Cerrar
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}
                          </div>
                        </div>
                      )}

                      {/* Rechazada/cancelada (no se toca) */}
                      {["rechazada", "cancelada"].includes(estado) && (
                        <div className="flex items-center gap-2 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2">
                          <Ban className="h-4 w-4" />
                          {estado === "rechazada"
                            ? "Esta reserva fue rechazada. No hay acciones disponibles."
                            : "Esta reserva fue cancelada."}
                        </div>
                      )}
                    </div>
                </div>
              ) : (
                /* === Crear reserva manual === */
                <div className="space-y-4">
                <div className="space-y-2">
                  <div className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-8">
                      <label className="block text-sm text-zinc-600 mb-1">Nombre del paciente</label>
                      <input
                        value={pacienteQuery}
                        onChange={(e) => setPacienteQuery(e.target.value)}
                        placeholder="Ej: Camila Pérez"
                        className="w-full border border-zinc-300 rounded-md px-3 py-2"
                      />
                    </div>

                    <div className="col-span-4">
                      <button
                        onClick={handlePacienteLookup}
                        disabled={loading || (pacienteQuery || "").trim().length < 2}
                        className="w-full px-3 py-2 rounded-lg bg-zinc-900 text-white hover:bg-zinc-800 disabled:opacity-60 transition"
                      >
                        {loading ? "Buscando..." : "Buscar"}
                      </button>
                    </div>
                  </div>

                  {/* Resultados */}
                  {pacienteResultados?.length > 0 && (
                    <div className="rounded-xl border border-zinc-200 bg-white shadow-sm overflow-hidden">
                      {pacienteResultados.map((u) => (
                        <button
                          key={u.id}
                          type="button"
                          onClick={() => seleccionarPaciente(u)}
                          className="w-full text-left px-3 py-2 hover:bg-zinc-50 transition border-b last:border-b-0"
                        >
                          <div className="text-sm font-medium text-zinc-800">
                            {`${u.nombres || ""} ${u.apellidos || ""}`.trim() || "(sin nombre)"}
                          </div>
                          <div className="text-xs text-zinc-500">
                            {(u.correo || u.email || "sin correo")}{u.rut ? ` · ${formatRut(u.rut)}` : ""}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Si buscó y no seleccionó nada, igual deja avanzar a llenado manual */}
                  {autofillDone && !pacienteOk && (pacienteResultados?.length === 0) && (
                    <div className="text-xs text-zinc-500">
                      No se encontró un paciente. Puedes completar los datos manualmente abajo.
                    </div>
                  )}
                </div>

                  {autofillDone && (
                    <>
                      <div className="grid sm:grid-cols-2 gap-3">
                        <Field label="Nombres">
                          <input
                            value={form.nombres}
                            onChange={(e) =>
                              setForm((f) => ({ ...f, nombres: e.target.value }))
                            }
                            className="w-full border border-zinc-300 rounded-md px-3 py-2"
                          />
                        </Field>

                        <Field label="Apellidos">
                          <input
                            value={form.apellidos}
                            onChange={(e) =>
                              setForm((f) => ({ ...f, apellidos: e.target.value }))
                            }
                            className="w-full border border-zinc-300 rounded-md px-3 py-2"
                          />
                        </Field>

                        <Field label="Correo electrónico" full>
                          <input
                            value={form.email}
                            onChange={(e) =>
                              setForm((f) => ({ ...f, email: e.target.value }))
                            }
                            className="w-full border border-zinc-300 rounded-md px-3 py-2"
                          />
                        </Field>

                          <div className="sm:col-span-2">
                            <ServicioVarianteSelector
                              serviciosFlat={serviciosFlat}
                              servicioId={form.servicioId}
                              modalidad={form.modalidad}
                              planSeleccionado={planSeleccionado} 
                              onServicioChange={(id, monto) =>
                                setForm((f) => ({ ...f, servicioId: id, monto }))
                              }
                              onModalidadChange={(mod, monto) =>
                                setForm((f) => ({ ...f, modalidad: mod, monto }))
                              }
                              limpiarCupon={limpiarCupon}
                              setPlanSeleccionado={setPlanSeleccionado}
                              setTipoUsoPlan={setTipoUsoPlan}
                            />
                          </div>

                          {/* Cupón: solo si NO hay plan activo seleccionado */}
                          {form.servicioId && !planSeleccionado && (
                            <Field label="Cupón de descuento" full>
                              <div className="flex gap-2">
                                <input
                                  value={cuponCode}
                                  onChange={(e) => {
                                    setCuponCode(e.target.value);
                                    setCuponInfo(null);
                                  }}
                                  placeholder="EJ: VERANO10"
                                  className="flex-1 border border-zinc-300 rounded-md px-3 py-2"
                                />
                                <button
                                  type="button"
                                  onClick={aplicarCuponManual}
                                  disabled={cuponLoading || !cuponCode.trim()}
                                  className="px-3 py-2 rounded-lg bg-zinc-900 text-white hover:bg-zinc-800 disabled:opacity-60"
                                >
                                  {cuponLoading ? "Aplicando..." : "Aplicar"}
                                </button>
                              </div>

                              {cuponMsg && (
                                <p className={`mt-2 text-sm ${cuponInfo ? "text-emerald-700" : "text-rose-700"}`}>
                                  {cuponMsg}
                                </p>
                              )}
                            </Field>
                          )}


                        {/* 🔵 selector de plan del paciente (si hay uno compatible) */}
                        {selectedServicio && pacienteUid && numeroSesiones > 1 && (
                          <Field label="Este servicio forma parte de un plan" full>
                            <div className="space-y-2 text-sm">

                              {/* ✔️ Mostrar planes usables */}
                              {planesUsables.length > 0 && (
                                <>
                                  <p className="text-blue-700 font-medium">Planes disponibles:</p>

                                  {planesUsables.map((p) => (
                                    <label key={p.id} className="flex items-center gap-2">
                                      <input
                                        type="radio"
                                        value={p.id}
                                        checked={planSeleccionado?.id === p.id}
                                        onChange={() => {
                                          setPlanSeleccionado(p);
                                          const pagado = (p.monto_restante ?? 0) <= 0;
                                          setTipoUsoPlan(pagado ? "incluida" : "valor_sesion");

                                          // ✅ Cupón no aplica cuando usas plan existente
                                          limpiarCupon();
                                        }}
                                      />
                                      <span>
                                        {(p.sesiones_usadas_calc ?? p.sesiones_usadas ?? 0) + (p.sesiones_reservadas_calc ?? p.sesiones_reservadas ?? 0)}
                                        / {sesionesTotalesPlan(p)} sesiones ocupadas
                                      </span>
                                    </label>
                                  ))}
                                </>
                              )}

                              {/* ✔️ Mostrar opción CREAR PLAN SOLO si la kine debe tener esa opción */}
                              {mostrarCrearPlanNuevo && (
                                <label className="flex items-center gap-2 mt-2">
                                  <input
                                    type="radio"
                                    value="crear_plan"
                                    checked={tipoUsoPlan === "crear_plan"}
                                    onChange={() => {
                                      setTipoUsoPlan("crear_plan");
                                      setPlanSeleccionado(null);
                                    }}
                                  />
                                  <span>Crear nuevo plan ({selectedServicio.variante})</span>
                                </label>
                              )}

                              {/* ❌ Si NO hay planes usables y NO se puede crear plan nuevo → no mostrar nada */}
                              {!planesUsables.length && !mostrarCrearPlanNuevo && (
                                <p className="text-zinc-500 text-sm">
                                  No hay planes disponibles para este servicio.
                                </p>
                              )}
                            </div>
                          </Field>
                        )}

                        {/* 🔵 Pago de plan nuevo */}
                        {tipoUsoPlan === "crear_plan" && (
                          <Field label="Forma de pago del plan">
                            <div className="space-y-2 text-sm">

                              <label className="flex items-center gap-2">
                                <input
                                  type="radio"
                                  value="plan_pago_completo"
                                  checked={form.pagoEstado === "plan_pago_completo"}
                                  onChange={() =>
                                    setForm((f) => ({ ...f, pagoEstado: "plan_pago_completo" }))
                                  }
                                />
                                <span>Pagar el total del plan ({clp(montoEfectivo)})</span>
                              </label>

                              <label className="flex items-center gap-2">
                                <input
                                  type="radio"
                                  value="plan_reserva"
                                  checked={form.pagoEstado === "plan_reserva"}
                                  onChange={() =>
                                    setForm((f) => ({ ...f, pagoEstado: "plan_reserva" }))
                                  }
                                />
                                <span>
                                  Pagar primera sesión ({clp(PRECIO_PRIMERA_SESION_PLAN)}) · Saldo: {clp(Math.max(0, montoEfectivo - PRECIO_PRIMERA_SESION_PLAN))}
                                </span>
                              </label>

                            </div>
                          </Field>
                        )}

                        <Field label="Profesional que atenderá" full>
                          <select
                            isSearchable={false}
                            value={profesionalId}
                            onChange={(e) => setProfesionalId(e.target.value)}
                            className="w-full border border-zinc-300 rounded-md px-3 py-2"
                          >
                            <option value="">Seleccione profesional…</option>
                            {profesionales.map((p) => (
                              <option key={p.id} value={p.id}>
                                {p.nombre}
                              </option>
                            ))}
                          </select>
                        </Field>

                        {/* Si NO hay plan seleccionado → selector clásico de pago */}
                        {!planSeleccionado && numeroSesiones === 1 && (
                          <Field label="Pago">
                            <div className="space-y-2 text-sm">
                              <label className="flex items-center gap-2">
                                <input
                                  type="radio"
                                  value="pago50"
                                  checked={form.pagoEstado === "pago50"}
                                  onChange={() =>
                                    setForm((f) => ({ ...f, pagoEstado: "pago50" }))
                                  }
                                />
                                <span>Paciente paga 50%</span>
                              </label>

                              <label className="flex items-center gap-2">
                                <input
                                  type="radio"
                                  value="pago100"
                                  checked={form.pagoEstado === "pago100"}
                                  onChange={() =>
                                    setForm((f) => ({ ...f, pagoEstado: "pago100" }))
                                  }
                                />
                                <span>Paciente paga 100%</span>
                              </label>
                            </div>
                          </Field>
                        )}
                        

                        {/* Si HAY plan seleccionado → opciones según estado del plan */}
                        {planSeleccionado && (
                          <Field label="Forma de pago del plan" full>
                            {(planSeleccionado.monto_restante ?? 0) > 0 ? (
                              <div className="space-y-2 text-sm">
                                <label className="flex items-center gap-2">
                                  <input
                                    type="radio"
                                    value="valor_sesion"
                                    checked={tipoUsoPlan === "valor_sesion"}
                                    onChange={(e) =>
                                      setTipoUsoPlan(e.target.value)
                                    }
                                  />
                                  <span>
                                    Pagó valor sesión{" "}
                                    {clp(
                                      calcularCobroPlanExistente(planSeleccionado, "valor_sesion")
                                    )}
                                  </span>
                                </label>
                                <label className="flex items-center gap-2">
                                  <input
                                    type="radio"
                                    value="saldo_plan"
                                    checked={tipoUsoPlan === "saldo_plan"}
                                    onChange={(e) =>
                                      setTipoUsoPlan(e.target.value)
                                    }
                                  />
                                  <span>
                                    Pagó saldo restante del plan{" "}
                                    {clp(planSeleccionado.monto_restante ?? 0)}
                                  </span>
                                </label>
                              </div>
                            ) : (
                              <div className="space-y-2 text-sm">
                                <label className="flex items-center gap-2">
                                  <input
                                    type="radio"
                                    value="incluida"
                                    checked={tipoUsoPlan === "incluida"}
                                    onChange={(e) =>
                                      setTipoUsoPlan(e.target.value)
                                    }
                                  />
                                  <span>
                                    Cliente ya pagó todo el plan (sesión incluida)
                                  </span>
                                </label>
                              </div>
                            )}
                          </Field>
                        )}

                        {/* 🔵 INFO DEL PLAN SELECCIONADO */}
                        {planSeleccionado && (
                          <div className="mt-3 p-3 rounded-xl bg-blue-50 border border-blue-100 text-sm">
                            <div className="font-semibold text-blue-800 mb-2">
                              Información del plan seleccionado
                            </div>

                            <p className="text-blue-900">
                              <strong>Sesiones totales:</strong> {sesionesTotalesPlan(planSeleccionado)}
                            </p>

                            <p className="text-blue-900">
                              <strong>Usadas:</strong> {planSeleccionado.sesiones_usadas}
                            </p>

                            <p className="text-blue-900">
                              <strong>Reservadas:</strong> {planSeleccionado.sesiones_reservadas}
                            </p>

                            <p className="text-blue-900">
                              <strong>Disponibles:</strong>{" "}
                              {planSeleccionado.sesiones_totales -
                                planSeleccionado.sesiones_usadas -
                                planSeleccionado.sesiones_reservadas}
                            </p>

                            <p className="text-blue-900">
                              <strong>Valor por sesión:</strong> {clp(planSeleccionado.precio_por_sesion)}
                            </p>

                            <p className="text-blue-900">
                              <strong>Total pagado:</strong> {clp(planSeleccionado.pago_total_realizado)}
                            </p>

                            <p className="text-blue-900">
                              <strong>Saldo del plan:</strong>{" "}
                              {clp(planSeleccionado.monto_restante ?? 0)}
                            </p>
                          </div>
                        )}
                      </div>

                                              {tipoUsoPlan === "crear_plan" && esServicioPareja && (
                        <div className="mt-3 p-4 rounded-xl border border-purple-200 bg-purple-50 text-sm">
                          <div className="font-semibold text-purple-900 mb-2">
                            🧑‍🤝‍🧑 Datos del acompañante (2do paciente)
                          </div>

                          <div className="grid sm:grid-cols-2 gap-3">
                            <Field label="Nombres acompañante">
                              <input
                                value={form.pareja.nombres}
                                onChange={(e) =>
                                  setForm((f) => ({
                                    ...f,
                                    pareja: { ...f.pareja, nombres: e.target.value },
                                  }))
                                }
                                className="w-full border border-zinc-300 rounded-md px-3 py-2"
                              />
                            </Field>

                            <Field label="Apellidos acompañante">
                              <input
                                value={form.pareja.apellidos}
                                onChange={(e) =>
                                  setForm((f) => ({
                                    ...f,
                                    pareja: { ...f.pareja, apellidos: e.target.value },
                                  }))
                                }
                                className="w-full border border-zinc-300 rounded-md px-3 py-2"
                              />
                            </Field>

                            <Field label="RUT acompañante" full>
                              <input
                                value={form.pareja.rut}
                                onChange={(e) =>
                                  setForm((f) => ({
                                    ...f,
                                    pareja: { ...f.pareja, rut: e.target.value },
                                  }))
                                }
                                placeholder="12.345.678-9"
                                className="w-full border border-zinc-300 rounded-md px-3 py-2"
                              />
                            </Field>

                            <Field label="Email acompañante" full>
                              <input
                                value={form.pareja.email}
                                onChange={(e) =>
                                  setForm((f) => ({
                                    ...f,
                                    pareja: { ...f.pareja, email: e.target.value },
                                  }))
                                }
                                className="w-full border border-zinc-300 rounded-md px-3 py-2"
                              />
                            </Field>

                            <Field label="Teléfono acompañante" full>
                              <input
                                value={form.pareja.telefono}
                                onChange={(e) =>
                                  setForm((f) => ({
                                    ...f,
                                    pareja: { ...f.pareja, telefono: e.target.value },
                                  }))
                                }
                                placeholder="+569..."
                                className="w-full border border-zinc-300 rounded-md px-3 py-2"
                              />
                            </Field>
                          </div>
                        </div>
                      )}



                      {/* Monto final — siempre visible al fondo */}
                        {form.servicioId && (
                          <div className="rounded-xl border border-zinc-200 bg-zinc-50 px-4 py-3 flex items-center justify-between">
                            <span className="text-sm text-zinc-500">
                              {planSeleccionado
                                ? "Monto a cobrar por esta reserva"
                                : cuponInfo ? "Monto original" : "Monto a cobrar"}
                            </span>
                            <span className="text-base font-medium text-zinc-800">
                              {clp(planSeleccionado ? cobroPlanExistente : Number(form.monto || 0))}
                            </span>
                          </div>
                        )}

                        {/* Si hay cupón, mostrar el total con descuento destacado */}
                        {cuponInfo && (
                          <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 flex items-center justify-between">
                            <div>
                              <span className="text-sm text-emerald-700 font-medium">Monto con descuento</span>
                              <span className="text-xs text-emerald-600 ml-2">(-{clp(cuponInfo.descuentoMonto)})</span>
                            </div>
                            <span className="text-lg font-medium text-emerald-800">
                              {clp(cuponInfo.montoFinal)}
                            </span>
                          </div>
                        )}

                        {/* Monto que paga el paciente para reservar */}
                          {form.servicioId && form.pagoEstado && (
                            <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 flex items-center justify-between">
                              <div>
                                <span className="text-sm text-blue-700 font-medium">Pago del paciente ahora</span>
                                <span className="text-xs text-blue-500 block">
                                  {planSeleccionado
                                    ? (tipoUsoPlan === "saldo_plan"
                                        ? "Saldo restante del plan"
                                        : cobroPlanExistente === 0
                                        ? "Sesión incluida en plan pagado"
                                        : "Valor de una sesión del plan")
                                    : form.pagoEstado === "pago50"
                                    ? "50% de reserva"
                                    : form.pagoEstado === "pago100"
                                    ? "Pago completo"
                                    : form.pagoEstado === "plan_pago_completo"
                                    ? "Pago total del plan"
                                    : form.pagoEstado === "plan_reserva"
                                    ? "Primera sesión"
                                    : "Pendiente de pago"}
                                </span>
                              </div>
                              <span className="text-lg font-medium text-blue-800">
                                {planSeleccionado
                                  ? clp(cobroPlanExistente)
                                  : form.pagoEstado === "pago50"
                                  ? clp(
                                      Math.round(
                                        (cuponInfo?.montoFinal ?? Number(form.monto || 0)) * 0.5
                                      )
                                    )
                                  : form.pagoEstado === "pago100"
                                  ? clp(cuponInfo?.montoFinal ?? Number(form.monto || 0))
                                  : form.pagoEstado === "plan_pago_completo"
                                  ? clp(cuponInfo?.montoFinal ?? Number(form.monto || 0))
                                  : form.pagoEstado === "plan_reserva"
                                  ? clp(
                                      Math.min(
                                        PRECIO_PRIMERA_SESION_PLAN,
                                        cuponInfo?.montoFinal ?? Number(form.monto || 0)
                                      )
                                    )
                                  : "$0"}
                              </span>
                            </div>
                          )}

                      <div className="pt-2 flex items-center justify-end gap-2">
                        <button
                          onClick={onClose}
                          className="px-3 py-2 rounded-lg bg-zinc-100 hover:bg-zinc-200 transition"
                        >
                          Cancelar
                        </button>
                        <button
                          onClick={handleReservar}
                          disabled={loading}
                          className="px-3 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition"
                        >
                          Reservar
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* REPROGRAMAR RESERVA: no cancela ni crea otra reserva */}
      {showReprogramar && reserva && (
        <div className="fixed inset-0 z-[21000] flex items-center justify-center bg-black/45 backdrop-blur-sm p-4">
          <div className="bg-white p-6 rounded-2xl w-full max-w-md shadow-2xl border border-zinc-200 space-y-4">
            <div className="flex items-center gap-2">
              <CalendarClock className="h-5 w-5 text-blue-700" />
              <h4 className="text-lg font-semibold text-zinc-800">Reprogramar reserva</h4>
            </div>
            <div className="rounded-xl bg-blue-50 p-3 text-sm text-blue-900">
              <p className="font-medium">{reserva.nombre || "Paciente"}</p>
              <p>Horario actual: <strong>{reserva.fecha} a las {reserva.hora}</strong></p>
              <p className="mt-1 text-xs">Se mantendrán el mismo ID, plan, pago, servicio y profesional.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="block text-sm font-medium text-zinc-700">
                Nueva fecha
                <input
                  type="date"
                  value={nuevaFechaReserva}
                  onChange={(e) => setNuevaFechaReserva(e.target.value)}
                  className="mt-1 w-full border border-zinc-300 rounded-lg px-3 py-2"
                />
              </label>
              <label className="block text-sm font-medium text-zinc-700">
                Nueva hora (24 h)
                <select
                  value={nuevaHoraReserva}
                  onChange={(e) => setNuevaHoraReserva(e.target.value)}
                  className="mt-1 w-full border border-zinc-300 rounded-lg px-3 py-2"
                >
                  <option value="">Seleccione una hora</option>
                  {Array.from({ length: 48 }, (_, i) => {
                    const h = `${String(Math.floor(i / 2)).padStart(2, "0")}:${i % 2 ? "30" : "00"}`;
                    return <option key={h} value={h}>{h}</option>;
                  })}
                </select>
              </label>
            </div>
            <label className="block text-sm font-medium text-zinc-700">
              Motivo (opcional)
              <textarea
                value={motivoReprogramacion}
                onChange={(e) => setMotivoReprogramacion(e.target.value)}
                rows={2}
                placeholder="Ej.: Solicitud del paciente"
                className="mt-1 w-full border border-zinc-300 rounded-lg px-3 py-2"
              />
            </label>
            <p className="text-xs text-zinc-500">
              Se comprobarán reservas existentes, cupos de grupo y bloqueos antes de guardar.
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setShowReprogramar(false)}
                disabled={loading}
                className="px-4 py-2 rounded-lg bg-zinc-100 hover:bg-zinc-200 disabled:opacity-50"
              >Cancelar</button>
              <button
                type="button"
                onClick={handleReprogramarReserva}
                disabled={loading || !nuevaFechaReserva || !nuevaHoraReserva}
                className="px-4 py-2 rounded-lg bg-blue-700 text-white hover:bg-blue-800 disabled:opacity-50"
              >{loading ? "Guardando..." : "Guardar nueva fecha"}</button>
            </div>
          </div>
        </div>
      )}

      {/* SUBMODAL: CANCELAR RESERVA */}
      {showSubModal && (
        <div className="fixed inset-0 z-[20000] flex items-center justify-center bg-black/40 backdrop-blur-sm animate-fadeIn">
          <div className="bg-white p-6 rounded-2xl w-full max-w-md shadow-xl border border-zinc-200">
            <h4 className="text-lg font-semibold text-zinc-800 mb-3">
              Cancelar reserva
            </h4>

            <div className="mb-3">
              <label className="block text-sm font-medium text-zinc-600 mb-1">
                Acción
              </label>
              <p className="px-3 py-2 rounded-md border border-zinc-300 bg-zinc-100 text-zinc-700">
                Cancelar definitivamente
              </p>
            </div>

            <div className="mb-3">
              <label className="block text-sm font-medium text-zinc-600 mb-1">
                Motivo
              </label>
              <textarea
                value={subMotivo}
                onChange={(e) => setSubMotivo(e.target.value)}
                placeholder="Describe brevemente el motivo..."
                rows={3}
                className="w-full border border-zinc-300 rounded-md px-3 py-2"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => setShowSubModal(false)}
                className="px-3 py-2 rounded-lg bg-zinc-100 hover:bg-zinc-200 transition"
              >
                Cerrar
              </button>

              <button
                onClick={confirmarSubModal}
                className="px-3 py-2 rounded-lg bg-rose-600 text-white hover:bg-rose-700 transition"
              >
                Confirmar cancelación
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL DE REEMBOLSO PARA PLANES */}
      {showReembolsoModal && reserva?.desdePlan && reserva?.planId && (
        <div className="fixed inset-0 z-[22000] flex items-center justify-center bg-black/40 backdrop-blur-sm animate-fadeIn">
          <div className="bg-white p-6 rounded-2xl w-full max-w-md shadow-xl border border-zinc-200">
            <h4 className="text-lg font-semibold text-zinc-800 mb-2">
              Reembolso del plan
            </h4>
            <p className="text-sm text-zinc-600 mb-3">
              Esta reserva pertenece a un <strong>plan de sesiones</strong> que ya fue
              cancelado.
            </p>

            {typeof reserva?.precio_por_sesion === "number" && (
              <p className="text-sm text-blue-900 mb-3">
                Valor de referencia por sesión:{" "}
                <strong>{clp(reserva.precio_por_sesion)}</strong>
              </p>
            )}

            <div className="space-y-2 text-sm">
              <button
                type="button"
                disabled={reembolsoLoading}
                  onClick={() => {
                    const ok = window.confirm(
                      "¿Estás seguro/a de NO registrar reembolso?\n\nEsto mantendrá los pagos del plan tal como están."
                    );
                    if (!ok) return;

                    setShowReembolsoModal(false);
                    onRefresh?.();
                    onClose?.();
                  }}
                className="w-full px-3 py-2 rounded-lg border border-zinc-300 bg-zinc-50 hover:bg-zinc-100 transition text-zinc-800 text-left"
              >
                No registrar reembolso (mantener pagos tal como están)
              </button>

              <button
                type="button"
                disabled={reembolsoLoading}
                onClick={async () => {
                  const valorSesion = reserva?.precio_por_sesion || 0;
                  if (!valorSesion) {
                    alert(
                      "Esta reserva no tiene 'precio_por_sesion' definido. Revisa el plan."
                    );
                    return;
                  }
                  const ok = window.confirm(
                    `¿Confirmas registrar un reembolso por ${clp(
                      valorSesion
                    )} en el plan?`
                  );
                  if (!ok) return;
                  await registrarReembolsoPlan(
                    valorSesion,
                    reembolsoMotivo || "Reembolso completo de sesión cancelada."
                  );
                }}
                className="w-full px-3 py-2 rounded-lg border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 transition text-emerald-800 text-left"
              >
                Registrar reembolso completo de esta sesión
              </button>
            </div>

            <div className="mt-4 pt-3 border-t border-zinc-100">
              <p className="text-sm font-medium text-zinc-700 mb-2">
                Reembolso parcial (monto diferente)
              </p>

              <div className="space-y-2">
                <input
                  type="number"
                  min="0"
                  value={reembolsoMonto}
                  onChange={(e) => setReembolsoMonto(e.target.value)}
                  placeholder="Monto a reembolsar en CLP"
                  className="w-full border border-zinc-300 rounded-md px-3 py-2 text-sm"
                />
                <textarea
                  rows={2}
                  value={reembolsoMotivo}
                  onChange={(e) => setReembolsoMotivo(e.target.value)}
                  placeholder="Motivo del reembolso (opcional pero recomendado)"
                  className="w-full border border-zinc-300 rounded-md px-3 py-2 text-sm"
                />
                <button
                  type="button"
                  disabled={reembolsoLoading}
                  onClick={async () => {
                    const raw = reembolsoMonto.replace(/[^\d]/g, "");
                    const montoNum = parseInt(raw || "0", 10);
                    if (!montoNum || Number.isNaN(montoNum)) {
                      alert("Ingresa un monto válido para el reembolso parcial.");
                      return;
                    }
                    const ok = window.confirm(
                      `¿Confirmas registrar un reembolso parcial por ${clp(
                        montoNum
                      )} en el plan?`
                    );
                    if (!ok) return;
                    await registrarReembolsoPlan(montoNum, reembolsoMotivo);
                  }}
                  className="w-full px-3 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition text-sm"
                >
                  {reembolsoLoading ? "Guardando..." : "Registrar reembolso parcial"}
                </button>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <button
                onClick={() => {
                  setShowReembolsoModal(false);
                  onClose?.();
                }}
                className="px-3 py-2 rounded-lg bg-zinc-100 hover:bg-zinc-200 transition text-sm"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {showReembolsoUI && (
        <div className="fixed inset-0 z-[26000] bg-black/40 backdrop-blur-sm flex items-center justify-center">
          <div className="bg-white p-6 rounded-2xl w-full max-w-md shadow-xl border border-zinc-200">

            <h3 className="text-lg font-semibold mb-3">Registrar reembolso</h3>

            <p className="text-sm mb-3">
              Indica el monto que se devolverá al paciente:
            </p>

            <input
              type="number"
              value={reembolsoMonto}
              onChange={(e) => setReembolsoMonto(e.target.value)}
              className="w-full border border-zinc-300 rounded-md px-3 py-2"
            />

            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setShowReembolsoUI(false)}
                className="px-3 py-2 rounded-lg bg-zinc-100 hover:bg-zinc-200 transition"
              >
                Cancelar
              </button>

              <button
                onClick={handleAprobarCancelacionConReembolso}
                className="px-3 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition"
              >
                Guardar
              </button>
            </div>

          </div>
        </div>
      )}

      {/* VISOR DE COMPROBANTE */}
      {zoomed && (
        <div
          className="fixed inset-0 z-[30000] bg-black/70 backdrop-blur-sm flex items-center justify-center animate-fadeIn"
          onClick={() => setZoomed(null)}
        >
          <img
            src={zoomed}
            alt="Comprobante ampliado"
            className="max-h-[90vh] max-w-[90vw] rounded-xl shadow-2xl ring-1 ring-white/30 object-contain"
          />
          <button
            onClick={() => setZoomed(null)}
            className="absolute top-5 right-5 bg-white/90 hover:bg-white text-zinc-800 rounded-full p-2 shadow-lg"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-5 left-1/2 -translate-x-1/2 z-[300]">
          <div className="rounded-full bg-zinc-900 text-white px-4 py-2 text-sm shadow-lg">
            {toast.text}
          </div>
        </div>
      )}
    </>
  );
}

/* ======================================================
    COMPONENTES AUXILIARES
====================================================== */
function Info({ label, value }) {
  return (
    <div className="space-y-0.5">
      <span className="text-zinc-500 text-sm">{label}:</span>
      <div className="text-zinc-800 text-sm font-medium break-words">
        {value || "—"}
      </div>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <label className={`block ${full ? "sm:col-span-2" : ""}`}>
      <span className="block text-sm text-zinc-600 mb-1">{label}</span>
      {children}
    </label>
  );
}
